import React, { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import notificationService from '@/utils/notificationService';
import apiClient from '@/utils/api';
import { Database, Download, RefreshCw, Shield, Calendar, HardDrive, Settings as SettingsIcon, Edit, Trash2, GitMerge, Plus, X, Check, ChevronDown } from 'lucide-react';
import FinanceSettings from './FinanceSettings';
import AgreementTemplatesManager from './AgreementTemplatesManager';
import PaymentSchedulesManager from './PaymentSchedulesManager';
import StudentWATINotifications from './StudentWATINotifications';
import MetaLeadFormsSettings from './MetaLeadFormsSettings';
import WebsiteFormBridgeSettings from './WebsiteFormBridgeSettings';
import CleanupSettings from './CleanupSettings';
import { IncentivePolicySettings } from './IncentivePolicySettings';
import { IntegrationHealthBadge } from './IntegrationHealthBadge';
import WorkflowSettings from './WorkflowSettings';
import IntakeManagement from './IntakeManagement';
import { useNotificationPreferences } from '@/hooks/useNotificationPreferences';

// Incoming-webhook health check for the AiSensy Gradbud channel. Shows whether
// AiSensy has actually POSTed anything to our server (and the latest payload),
// which is the meaningful "connection test" for an inbound webhook.
const AiSensyWebhookCheck = () => {
  const [checking, setChecking] = useState(false);
  const [data, setData] = useState(null);
  const [showRaw, setShowRaw] = useState(false);

  const timeAgo = (iso) => {
    if (!iso) return '';
    const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  const check = async () => {
    setChecking(true);
    setData(null);
    try {
      const res = await apiClient.get('/webhook/aisensy-gradbud/debug?limit=5');
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to check webhook activity.');
    } finally {
      setChecking(false);
    }
  };

  const latest = data?.payloads?.[0];

  return (
    <div className="border-t pt-4 mt-4">
      <div className="flex items-center justify-between gap-2 mb-2">
        <h4 className="font-medium text-gray-900 text-sm">
          <i className="fas fa-wave-square mr-2 text-gray-500"></i>
          Incoming Webhook Activity
        </h4>
        <Button
          size="sm"
          variant="outline"
          onClick={check}
          disabled={checking}
          className="text-xs h-8"
          data-testid="aisensy-check-webhook-btn"
        >
          {checking ? (<><i className="fas fa-spinner fa-spin mr-1"></i>Checking…</>) : (<><i className="fas fa-rotate mr-1"></i>Check Webhook Activity</>)}
        </Button>
      </div>

      {data && (
        <div className="text-sm" data-testid="aisensy-webhook-status">
          {data.count === 0 ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-800">
              <p className="font-medium"><i className="fas fa-circle-xmark mr-2"></i>No webhook received yet</p>
              <p className="mt-1 text-red-700">AiSensy hasn't reached this server. Check in the AiSensy dashboard that the webhook is <strong>Active</strong>, subscribed to <strong>Incoming Messages</strong>, and uses this exact URL (ending in <code>/api/webhook/aisensy-gradbud</code>). Then send a test WhatsApp and check again.</p>
            </div>
          ) : (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-green-900">
              <p className="font-medium"><i className="fas fa-circle-check mr-2"></i>Connected — AiSensy is reaching this server</p>
              <p className="mt-1">Last message received <strong>{timeAgo(latest?.received_at)}</strong> ({new Date(latest?.received_at).toLocaleString()}). {data.count} payload(s) stored (last 100).</p>
              <button
                className="text-green-700 underline mt-2 text-xs"
                onClick={() => setShowRaw(v => !v)}
                data-testid="aisensy-toggle-raw"
              >
                {showRaw ? 'Hide' : 'Show'} latest raw payload
              </button>
              {showRaw && (
                <pre className="mt-2 bg-white border rounded p-2 text-xs overflow-auto max-h-64 text-gray-700">
{JSON.stringify(latest?.payload, null, 2)}
                </pre>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// One-time maintenance: repair leads that share the same Student ID (the old
// generator got stuck at STU-2026-10000 across ALL sources). Admin only.
const FixStudentIdsCard = () => {

  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const runFix = async () => {
    if (!window.confirm('Repair duplicate Student IDs? This keeps the earliest lead in each group and assigns fresh unique IDs to the rest. Safe to run multiple times.')) return;
    setRunning(true);
    setResult(null);
    try {
      const res = await apiClient.post('/admin/fix-duplicate-student-ids');
      setResult(res.data);
      if (res.data.reassigned_count > 0) {
        toast.success(`Fixed ${res.data.reassigned_count} leads across ${res.data.duplicate_groups} duplicate group(s).`);
      } else {
        toast.success('No duplicate Student IDs found — everything is already unique.');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to run the repair. Please try again.');
    } finally {
      setRunning(false);
    }
  };

  return (
    <Card data-testid="fix-student-ids-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-amber-600" />
          Data Maintenance — Duplicate Student IDs
        </CardTitle>
        <CardDescription>
          Repairs leads (from any source) that were assigned the same Student ID, e.g. multiple leads stuck on STU-2026-10000. Keeps the earliest lead in each group and renumbers the rest with fresh unique IDs.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <Button
          data-testid="fix-student-ids-btn"
          onClick={runFix}
          disabled={running}
          className="bg-amber-600 hover:bg-amber-700 text-white"
        >
          {running ? (<><RefreshCw className="h-4 w-4 mr-2 animate-spin" />Repairing…</>) : (<><RefreshCw className="h-4 w-4 mr-2" />Fix Duplicate Student IDs</>)}
        </Button>
        {result && (
          <div className="text-sm bg-gray-50 border rounded-lg p-3" data-testid="fix-student-ids-result">
            <p><strong>Duplicate groups:</strong> {result.duplicate_groups}</p>
            <p><strong>Leads renumbered:</strong> {result.reassigned_count}</p>
            {result.reassigned_count > 0 && (
              <p className="text-gray-500 mt-1">Refresh the Leads page to see the updated Student IDs.</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};


// Consistent brand colors from logo
const COLORS = {
  primary: '#192650',
  accent: '#0DA891',
  background: '#F8FAFC',
  border: '#E2E8F0'
};

// Meta Integration Components

// Catalog Management Component (Programs & Universities)
const CatalogManagement = () => {
  const [programs, setPrograms] = useState([]);
  const [programCommissions, setProgramCommissions] = useState({});
  const [commissionDirty, setCommissionDirty] = useState(false);
  const [commissionSaving, setCommissionSaving] = useState(false);
  const [universities, setUniversities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('programs');

  // Rename state
  const [renameDialog, setRenameDialog] = useState(false);
  const [renameType, setRenameType] = useState('');
  const [renameOld, setRenameOld] = useState('');
  const [renameNew, setRenameNew] = useState('');
  const [renameLoading, setRenameLoading] = useState(false);

  // Merge state
  const [mergeDialog, setMergeDialog] = useState(false);
  const [mergeType, setMergeType] = useState('');
  const [mergeSelected, setMergeSelected] = useState([]);
  const [mergeTarget, setMergeTarget] = useState('');
  const [mergeLoading, setMergeLoading] = useState(false);

  // Add state
  const [addDialog, setAddDialog] = useState(false);
  const [addType, setAddType] = useState('');
  const [addName, setAddName] = useState('');
  const [addLoading, setAddLoading] = useState(false);

  // Find duplicates state
  const [dupDialog, setDupDialog] = useState(false);
  const [dupGroups, setDupGroups] = useState([]);  // [{variants:[], suggested_canonical, target}]
  const [dupScanning, setDupScanning] = useState(false);
  const [dupMerging, setDupMerging] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/constants');
      setPrograms(response.data.programs || []);
      setUniversities(response.data.universities || []);
      // Also fetch per-program commission overrides
      try {
        const cRes = await apiClient.get('/constants/programs/commissions');
        setProgramCommissions(cRes.data?.commissions || {});
        setCommissionDirty(false);
      } catch (e) { /* non-admin roles may get 403, ignore */ }
    } catch (error) {
      toast.error('Failed to fetch catalog data');
    } finally {
      setLoading(false);
    }
  };
  
  const saveProgramCommissions = async () => {
    setCommissionSaving(true);
    try {
      await apiClient.put('/constants/programs/commissions', { commissions: programCommissions });
      toast.success('Per-program commissions saved');
      setCommissionDirty(false);
    } catch (e) {
      toast.error('Failed to save commissions');
    } finally {
      setCommissionSaving(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const items = activeSection === 'programs' ? programs : universities;

  const handleRename = async () => {
    if (!renameNew.trim()) return;
    setRenameLoading(true);
    try {
      await apiClient.put(`/constants/${renameType}/rename`, { old_name: renameOld, new_name: renameNew.trim() });
      toast.success(`Renamed "${renameOld}" to "${renameNew.trim()}"`);
      setRenameDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to rename');
    } finally {
      setRenameLoading(false);
    }
  };

  const handleDelete = async (type, name) => {
    if (!window.confirm(`Remove "${name}" from the ${type} list? This won't affect existing student records.`)) return;
    try {
      await apiClient.delete(`/constants/${type}/${encodeURIComponent(name)}`);
      toast.success(`"${name}" removed`);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleMerge = async () => {
    if (!mergeTarget || mergeSelected.length < 2) return;
    setMergeLoading(true);
    try {
      const response = await apiClient.post(`/constants/${mergeType}/merge`, { sources: mergeSelected, target: mergeTarget });
      toast.success(response.data.message);
      setMergeDialog(false);
      setMergeSelected([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to merge');
    } finally {
      setMergeLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!addName.trim()) return;
    setAddLoading(true);
    try {
      await apiClient.post(`/constants/${addType}`, addType === 'programs' ? { program: addName.trim() } : { university: addName.trim() });
      toast.success(`"${addName.trim()}" added`);
      setAddDialog(false);
      setAddName('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add');
    } finally {
      setAddLoading(false);
    }
  };

  const openRename = (type, name) => {
    setRenameType(type);
    setRenameOld(name);
    setRenameNew(name);
    setRenameDialog(true);
  };

  const openMerge = (type) => {
    setMergeType(type);
    setMergeSelected([]);
    setMergeTarget('');
    setMergeDialog(true);
  };

  const openAdd = (type) => {
    setAddType(type);
    setAddName('');
    setAddDialog(true);
  };

  const openFindDuplicates = async () => {
    setDupScanning(true);
    setDupDialog(true);
    try {
      const { data } = await apiClient.get(`/constants/${activeSection}/duplicates`);
      setDupGroups((data.duplicate_groups || []).map(g => ({
        variants: g.variants,
        suggested_canonical: g.suggested_canonical,
        target: g.suggested_canonical,
      })));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to scan for duplicates');
      setDupDialog(false);
    } finally {
      setDupScanning(false);
    }
  };

  const applyAutoMerge = async () => {
    if (dupGroups.length === 0) return;
    setDupMerging(true);
    try {
      const payload = { groups: dupGroups.map(g => ({ sources: g.variants, target: g.target })) };
      const { data } = await apiClient.post(`/constants/${activeSection}/auto-merge-duplicates`, payload);
      toast.success(`${data.message}. ${data.students_updated + data.portals_updated} records updated.`);
      setDupDialog(false);
      setDupGroups([]);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to merge duplicates');
    } finally {
      setDupMerging(false);
    }
  };

  const toggleMergeItem = (name) => {
    setMergeSelected(prev => prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]);
    if (mergeTarget === name) setMergeTarget('');
  };

  return (
    <div className="space-y-4">
      {/* Section Toggle */}
      <div className="flex items-center gap-2">
        <Button
          variant={activeSection === 'programs' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveSection('programs')}
          style={activeSection === 'programs' ? { backgroundColor: COLORS.accent } : {}}
          data-testid="catalog-programs-btn"
        >
          Programs ({programs.length})
        </Button>
        <Button
          variant={activeSection === 'universities' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveSection('universities')}
          style={activeSection === 'universities' ? { backgroundColor: COLORS.accent } : {}}
          data-testid="catalog-universities-btn"
        >
          Universities ({universities.length})
        </Button>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" size="sm" onClick={() => openAdd(activeSection)} data-testid="catalog-add-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={openFindDuplicates}
            data-testid="catalog-find-duplicates-btn"
            className="border-amber-300 text-amber-700 hover:bg-amber-50"
          >
            <i className="fas fa-search mr-1.5 text-[11px]"></i> Find Duplicates
          </Button>
          <Button variant="outline" size="sm" onClick={() => openMerge(activeSection)} disabled={items.length < 2} data-testid="catalog-merge-btn">
            <GitMerge className="w-3.5 h-3.5 mr-1" /> Merge Manually
          </Button>
        </div>
      </div>

      {/* Items List */}
      <Card>
        <CardHeader className="p-4 pb-2">
          <div className="flex items-center justify-between gap-2">
            <div>
              <CardTitle className="text-base">{activeSection === 'programs' ? 'Programs' : 'Universities'}</CardTitle>
              <CardDescription className="text-xs">
                {activeSection === 'programs' 
                  ? 'Programs offered to students' 
                  : 'Manage university names to prevent duplicate entries'}
              </CardDescription>
            </div>
            {false && activeSection === 'programs' && commissionDirty && (
              <Button size="sm" onClick={saveProgramCommissions} disabled={commissionSaving} data-testid="save-commissions-btn" className="bg-indigo-600 hover:bg-indigo-700 text-white">
                <i className={`fas ${commissionSaving ? 'fa-spinner fa-spin' : 'fa-save'} mr-1.5 text-xs`}></i>
                Save commissions
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-4 pt-2">
          {loading ? (
            <div className="flex items-center justify-center py-8 text-gray-400 text-sm">Loading...</div>
          ) : items.length === 0 ? (
            <div className="flex items-center justify-center py-8 text-gray-400 text-sm">No {activeSection} found</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {items.map((item) => (
                <div key={item} className="flex items-center justify-between px-3 py-2 rounded-lg border border-gray-100 bg-gray-50 hover:bg-gray-100 transition-colors group">
                  <div className="flex-1 min-w-0 mr-2">
                    <div className="text-sm truncate">{item}</div>
                    {false && activeSection === 'programs' && (
                      <div className="flex items-center gap-1 mt-1">
                        <span className="text-[10px] text-gray-500">₹</span>
                        <Input
                          type="number"
                          min="0"
                          step="500"
                          placeholder="5000"
                          value={programCommissions[item] ?? ''}
                          onChange={(e) => {
                            const v = e.target.value;
                            setProgramCommissions((prev) => ({ ...prev, [item]: v === '' ? '' : parseFloat(v) }));
                            setCommissionDirty(true);
                          }}
                          className="h-6 w-24 text-[11px] px-2"
                          data-testid={`commission-input-${item}`}
                          title="Base commission (₹) paid to the counsellor when this program's student is marked as flown"
                        />
                        <span className="text-[10px] text-gray-400 ml-1">base</span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => openRename(activeSection, item)}
                      className="p-1 rounded hover:bg-white text-gray-400 hover:text-blue-600"
                      title="Rename"
                      data-testid={`rename-${item}`}
                    >
                      <Edit className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => handleDelete(activeSection, item)}
                      className="p-1 rounded hover:bg-white text-gray-400 hover:text-red-600"
                      title="Remove from list"
                      data-testid={`delete-${item}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Rename Dialog */}
      <Dialog open={renameDialog} onOpenChange={setRenameDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Rename {renameType === 'programs' ? 'Program' : 'University'}</DialogTitle>
            <DialogDescription>This will update all existing student records using the old name.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs text-gray-500">Current Name</Label>
              <p className="text-sm font-medium">{renameOld}</p>
            </div>
            <div>
              <Label>New Name</Label>
              <Input value={renameNew} onChange={(e) => setRenameNew(e.target.value)} className="mt-1" data-testid="rename-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameDialog(false)}>Cancel</Button>
            <Button onClick={handleRename} disabled={renameLoading || !renameNew.trim() || renameNew === renameOld} style={{ backgroundColor: COLORS.accent }} data-testid="rename-confirm-btn">
              {renameLoading ? 'Renaming...' : 'Rename'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Dialog */}
      <Dialog open={addDialog} onOpenChange={setAddDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add {addType === 'programs' ? 'Program' : 'University'}</DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <Label>Name</Label>
            <Input value={addName} onChange={(e) => setAddName(e.target.value)} placeholder={addType === 'programs' ? 'e.g. BBA' : 'e.g. Harvard University'} className="mt-1" data-testid="add-name-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialog(false)}>Cancel</Button>
            <Button onClick={handleAdd} disabled={addLoading || !addName.trim()} style={{ backgroundColor: COLORS.accent }} data-testid="add-confirm-btn">
              {addLoading ? 'Adding...' : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Merge Dialog */}
      <Dialog open={mergeDialog} onOpenChange={setMergeDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GitMerge className="w-5 h-5 text-indigo-600" />
              Merge {mergeType === 'programs' ? 'Programs' : 'Universities'}
            </DialogTitle>
            <DialogDescription>Select items to merge, then choose which name to keep. All student records will be updated.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2 max-h-[400px] overflow-y-auto">
            <Label className="text-xs text-gray-500">Select items to merge (at least 2):</Label>
            <div className="space-y-1">
              {(mergeType === 'programs' ? programs : universities).map((item) => (
                <label key={item} className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                  mergeSelected.includes(item) ? 'border-indigo-300 bg-indigo-50' : 'border-gray-100 hover:bg-gray-50'
                }`}>
                  <input
                    type="checkbox"
                    checked={mergeSelected.includes(item)}
                    onChange={() => toggleMergeItem(item)}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm flex-1">{item}</span>
                  {mergeSelected.includes(item) && (
                    <button
                      onClick={(e) => { e.preventDefault(); setMergeTarget(item); }}
                      className={`text-[10px] px-2 py-0.5 rounded-full border ${
                        mergeTarget === item ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-300 hover:border-indigo-400'
                      }`}
                    >
                      {mergeTarget === item ? 'Keep this name' : 'Use as target'}
                    </button>
                  )}
                </label>
              ))}
            </div>
            {mergeSelected.length >= 2 && !mergeTarget && (
              <p className="text-xs text-amber-600">Click "Use as target" to choose which name to keep</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMergeDialog(false)}>Cancel</Button>
            <Button
              onClick={handleMerge}
              disabled={mergeLoading || mergeSelected.length < 2 || !mergeTarget}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="merge-confirm-btn"
            >
              {mergeLoading ? 'Merging...' : `Merge ${mergeSelected.length} into "${mergeTarget || '...'}"`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Find Duplicates Dialog */}
      <Dialog open={dupDialog} onOpenChange={(open) => { if (!dupMerging) setDupDialog(open); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <i className="fas fa-search text-amber-600"></i>
              Find &amp; Merge Duplicate {activeSection === 'programs' ? 'Programs' : 'Universities'}
            </DialogTitle>
            <DialogDescription>
              Auto-scans catalog + student records for case/whitespace duplicates (e.g. <em>Nursing / nursing</em>, <em>BSc Nursing / Bsc Nursing</em>).
              Review each group, adjust the canonical name if needed, then click <strong>Merge All</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2 max-h-[420px] overflow-y-auto">
            {dupScanning ? (
              <div className="py-10 text-center text-gray-400">
                <i className="fas fa-spinner fa-spin mr-2"></i>Scanning for duplicates...
              </div>
            ) : dupGroups.length === 0 ? (
              <div className="py-10 text-center" data-testid="no-duplicates">
                <i className="fas fa-check-circle text-3xl text-emerald-500 mb-2"></i>
                <p className="text-sm font-semibold text-gray-700">No duplicates found</p>
                <p className="text-xs text-gray-500 mt-1">Your {activeSection} catalog is clean.</p>
              </div>
            ) : (
              <>
                <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-[11px] text-amber-800">
                  <i className="fas fa-info-circle mr-1.5"></i>
                  Found <strong>{dupGroups.length}</strong> duplicate group{dupGroups.length !== 1 ? 's' : ''}. The suggested canonical name is auto-picked (prefers proper case like <em>BSc</em> over <em>bsc</em>). Change it if you prefer a different one.
                </div>
                {dupGroups.map((g, idx) => (
                  <div key={idx} className="rounded-lg border bg-white p-3 space-y-2" data-testid={`dup-group-${idx}`}>
                    <div className="flex items-center justify-between">
                      <div className="text-[11px] text-gray-500">Group {idx + 1} — merging {g.variants.length} variants</div>
                      <button
                        type="button"
                        onClick={() => setDupGroups(prev => prev.filter((_, i) => i !== idx))}
                        className="text-[11px] text-gray-400 hover:text-red-600"
                        title="Skip this group"
                      >
                        <i className="fas fa-times"></i> Skip
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {g.variants.map(v => (
                        <span key={v} className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full border border-gray-200">
                          {v}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center gap-2 pt-1">
                      <Label className="text-[11px] text-gray-500 min-w-[100px]">Keep as:</Label>
                      <select
                        value={g.target}
                        onChange={(e) => {
                          const val = e.target.value;
                          setDupGroups(prev => prev.map((x, i) => i === idx ? { ...x, target: val } : x));
                        }}
                        className="flex-1 h-8 text-sm border border-gray-300 rounded px-2 bg-white focus:outline-none focus:border-indigo-400"
                        data-testid={`dup-group-${idx}-target`}
                      >
                        {g.variants.map(v => (
                          <option key={v} value={v}>{v}{v === g.suggested_canonical ? ' (suggested)' : ''}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDupDialog(false)} disabled={dupMerging}>
              {dupGroups.length === 0 ? 'Close' : 'Cancel'}
            </Button>
            {dupGroups.length > 0 && (
              <Button
                onClick={applyAutoMerge}
                disabled={dupMerging}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="dup-merge-all-btn"
              >
                {dupMerging ? <><i className="fas fa-spinner fa-spin mr-2"></i>Merging...</> : <><i className="fas fa-magic mr-2"></i>Merge All ({dupGroups.length} group{dupGroups.length !== 1 ? 's' : ''})</>}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};


const MetaConnectionStatus = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const response = await apiClient.get('/meta/settings');
        setStatus(response.data);
      } catch (error) {
        console.error('Error fetching Meta status:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStatus();
  }, []);
  
  if (loading) return <Badge variant="outline">Loading...</Badge>;
  
  if (!status?.enabled) {
    return <Badge variant="secondary">Disabled</Badge>;
  }
  
  if (status?.connection_status === 'connected') {
    return (
      <Badge variant="outline" className="bg-green-100 text-green-800">
        Previously verified: {status.connected_user}
      </Badge>
    );
  }
  
  return <Badge variant="outline" className="bg-yellow-100 text-yellow-800">Not Connected</Badge>;
};

// Salestrail Connection Status Component
const SalestrailConnectionStatus = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const response = await apiClient.get('/salestrail/settings');
        console.log('Salestrail settings response:', response.data);
        setStatus(response.data);
      } catch (error) {
        console.error('Error fetching Salestrail status:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStatus();
  }, []);
  
  if (loading) return <Badge variant="outline">Loading...</Badge>;
  
  // Check enabled status - handle both boolean and string
  const isEnabled = status?.enabled === true || status?.enabled === 'true';
  
  if (!isEnabled) {
    return <Badge variant="secondary">Disabled</Badge>;
  }
  
  if (status?.connection_status === 'connected') {
    return (
      <Badge variant="outline" className="bg-green-100 text-green-800">
        <i className="fas fa-check-circle mr-1"></i>Previously verified
      </Badge>
    );
  }
  
  return <Badge variant="outline" className="bg-yellow-100 text-yellow-800">Not Connected</Badge>;
};

const MetaIntegrationSettings = () => {
  const [settings, setSettings] = useState({
    enabled: false,
    access_token: '',
    ad_account_id: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showToken, setShowToken] = useState(false);
  
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await apiClient.get('/meta/settings');
        setSettings({
          enabled: response.data.enabled || false,
          access_token: '', // Don't show actual token
          ad_account_id: response.data.ad_account_id || ''
        });
      } catch (error) {
        console.error('Error fetching Meta settings:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);
  
  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        enabled: settings.enabled,
        ad_account_id: settings.ad_account_id
      };
      // Only include token if user entered a new one
      if (settings.access_token) {
        payload.access_token = settings.access_token;
      }
      await apiClient.put('/meta/settings', payload);
      toast.success('Meta settings saved successfully!');
      setSettings(prev => ({ ...prev, access_token: '' })); // Clear token field
    } catch (error) {
      toast.error('Failed to save Meta settings');
    } finally {
      setSaving(false);
    }
  };
  
  const handleTestConnection = async () => {
    setTesting(true);
    try {
      const response = await apiClient.post('/meta/test-connection');
      if (response.data.success) {
        toast.success(`Connected successfully as: ${response.data.user_name}`);
      } else {
        toast.error(`Connection failed: ${response.data.error}`);
      }
    } catch (error) {
      toast.error('Failed to test connection');
    } finally {
      setTesting(false);
    }
  };
  
  if (loading) {
    return <div className="text-center py-4"><i className="fas fa-spinner fa-spin mr-2"></i>Loading...</div>;
  }
  
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <Label className="font-medium">Enable Meta Campaign Tracking</Label>
          <p className="text-sm text-muted-foreground">Automatically fetch campaign data for CTWA leads</p>
        </div>
        <Switch
          checked={settings.enabled}
          onCheckedChange={(checked) => setSettings({ ...settings, enabled: checked })}
        />
      </div>
      
      {/* Setup Guide */}
      <details className="rounded-lg border border-blue-200 bg-blue-50/60 p-3 group" data-testid="meta-setup-guide">
        <summary className="cursor-pointer text-sm font-semibold text-blue-900 flex items-center gap-2 list-none">
          <i className="fas fa-chevron-right transition-transform group-open:rotate-90"></i>
          <i className="fas fa-book-open"></i>
          How to set up Meta Marketing API (step-by-step)
        </summary>
        <div className="mt-3 space-y-3 text-[12px] text-blue-900/90 leading-relaxed">
          <div>
            <div className="font-semibold mb-1">1. Get your Meta Ad Account ID</div>
            <ul className="list-disc pl-5 space-y-0.5">
              <li>Go to <a href="https://business.facebook.com/settings/ad-accounts" target="_blank" rel="noopener noreferrer" className="text-blue-700 underline">Business Settings → Ad Accounts</a></li>
              <li>Copy the numeric ID shown (e.g. <code className="bg-white px-1 rounded">act_3818387038413270</code> — paste the part after <code>act_</code> above).</li>
            </ul>
          </div>
          <div>
            <div className="font-semibold mb-1">2. Generate a long-lived Access Token with <code>ads_read</code> permission</div>
            <ul className="list-disc pl-5 space-y-0.5">
              <li>Open <a href="https://developers.facebook.com/tools/explorer/" target="_blank" rel="noopener noreferrer" className="text-blue-700 underline">Graph API Explorer</a>.</li>
              <li>In the top-right, select your Meta App (or create one first at <a href="https://developers.facebook.com/apps" target="_blank" rel="noopener noreferrer" className="text-blue-700 underline">developers.facebook.com/apps</a>).</li>
              <li>Click <strong>Add a Permission → ads_read</strong>.</li>
              <li>Click <strong>Generate Access Token</strong>, approve the Business you want to track.</li>
              <li>Copy the token and paste it below. Tokens expire in 60 days by default — use the <a href="https://developers.facebook.com/tools/debug/accesstoken/" target="_blank" rel="noopener noreferrer" className="text-blue-700 underline">Access Token Debugger</a> → <strong>Extend Access Token</strong> for a long-lived one (≈60 days).</li>
            </ul>
          </div>
          <div>
            <div className="font-semibold mb-1">3. Save & Test</div>
            <p>Click <strong>Save Settings</strong>, then <strong>Test Connection</strong>. A ✓ means our server can read campaigns/ads from your account.</p>
          </div>
          <div className="rounded bg-amber-100/70 border border-amber-200 p-2 text-amber-900 text-[11px]">
            <i className="fas fa-exclamation-triangle mr-1"></i>
            <strong>Security:</strong> Never share your access token. This CRM stores it encrypted; it's only sent to Meta's official Graph API endpoints.
          </div>
        </div>
      </details>
      
      <div className="space-y-2">
        <Label>Meta Ad Account ID</Label>
        <Input
          value={settings.ad_account_id}
          onChange={(e) => setSettings({ ...settings, ad_account_id: e.target.value })}
          placeholder="e.g., 3818387038413270"
        />
        <p className="text-xs text-muted-foreground">Your Meta Ad Account ID (without "act_" prefix)</p>
      </div>
      
      <div className="space-y-2">
        <Label>Access Token</Label>
        <div className="flex gap-2">
          <Input
            type={showToken ? "text" : "password"}
            value={settings.access_token}
            onChange={(e) => setSettings({ ...settings, access_token: e.target.value })}
            placeholder="Enter new token to update (leave empty to keep current)"
          />
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setShowToken(!showToken)}
          >
            <i className={`fas fa-eye${showToken ? '-slash' : ''}`}></i>
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Generate from <a href="https://developers.facebook.com/tools/explorer/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Graph API Explorer</a> with ads_read permission
        </p>
      </div>
      
      <div className="flex gap-2">
        <Button onClick={handleSave} disabled={saving}>
          {saving ? <><i className="fas fa-spinner fa-spin mr-2"></i>Saving...</> : <><i className="fas fa-save mr-2"></i>Save Settings</>}
        </Button>
        <Button variant="outline" onClick={handleTestConnection} disabled={testing}>
          {testing ? <><i className="fas fa-spinner fa-spin mr-2"></i>Testing...</> : <><i className="fas fa-plug mr-2"></i>Test Connection</>}
        </Button>
      </div>
    </div>
  );
};

// Salestrail Call Tracking Component - Push API Only
const SalestrailIntegrationSettings = () => {
  const [settings, setSettings] = useState({
    enabled: false,
    webhook_username: 'patrickscrm',
    webhook_password: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showWebhookPassword, setShowWebhookPassword] = useState(false);
  const [lastWebhook, setLastWebhook] = useState(null);
  
  // Get the webhook URL for production
  const getWebhookUrl = () => {
    const baseUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    return `${baseUrl}/api/salestrail/webhook`;
  };
  
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await apiClient.get('/salestrail/settings');
        setSettings({
          enabled: response.data.enabled === true || response.data.enabled === 'true',
          webhook_username: response.data.webhook_username || 'patrickscrm',
          webhook_password: ''
        });
        setLastWebhook(response.data.last_webhook);
      } catch (error) {
        console.error('Error fetching Salestrail settings:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);
  
  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        enabled: settings.enabled,
        webhook_username: settings.webhook_username
      };
      if (settings.webhook_password) {
        payload.webhook_password = settings.webhook_password;
      }
      await apiClient.put('/salestrail/settings', payload);
      toast.success('Salestrail settings saved!');
      setSettings(prev => ({ ...prev, webhook_password: '' }));
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };
  
  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard!`);
  };
  
  if (loading) {
    return <div className="text-center py-4"><i className="fas fa-spinner fa-spin mr-2"></i>Loading...</div>;
  }
  
  return (
    <div className="space-y-6">
      {/* Integration Toggle */}
      <div className="flex items-center justify-between">
        <div>
          <Label className="font-medium">Enable Call Tracking</Label>
          <p className="text-sm text-muted-foreground">Receive calls in real-time via Push API</p>
        </div>
        <Switch
          checked={settings.enabled}
          onCheckedChange={(checked) => setSettings({ ...settings, enabled: checked })}
        />
      </div>
      
      {/* Setup Guide */}
      <details className="rounded-lg border border-blue-200 bg-blue-50/60 p-3 group" data-testid="salestrail-setup-guide">
        <summary className="cursor-pointer text-sm font-semibold text-blue-900 flex items-center gap-2 list-none">
          <i className="fas fa-chevron-right transition-transform group-open:rotate-90"></i>
          <i className="fas fa-book-open"></i>
          How to set up Salestrail (step-by-step)
        </summary>
        <div className="mt-3 space-y-3 text-[12px] text-blue-900/90 leading-relaxed">
          <div>
            <div className="font-semibold mb-1">1. Enable Push API in Salestrail</div>
            <ul className="list-disc pl-5 space-y-0.5">
              <li>Sign in to <a href="https://app.salestrail.io" target="_blank" rel="noopener noreferrer" className="text-blue-700 underline">Salestrail Dashboard</a>.</li>
              <li>Go to <strong>Settings → Integrations → Push API</strong>.</li>
              <li>Toggle <strong>Enable Push API</strong> ON.</li>
            </ul>
          </div>
          <div>
            <div className="font-semibold mb-1">2. Paste the Webhook URL</div>
            <ul className="list-disc pl-5 space-y-0.5">
              <li>Below, you'll see a <strong>Webhook URL</strong> auto-generated for your CRM. Click the copy icon.</li>
              <li>In Salestrail's Push API settings, paste it into the <em>Webhook URL</em> field.</li>
            </ul>
          </div>
          <div>
            <div className="font-semibold mb-1">3. Set Matching Credentials</div>
            <ul className="list-disc pl-5 space-y-0.5">
              <li>Choose any <strong>Username</strong> (default: <code className="bg-white px-1 rounded">patrickscrm</code>) and a strong <strong>Password</strong>.</li>
              <li>Enter the exact same Username + Password on both sides — here in the CRM <em>and</em> in Salestrail.</li>
            </ul>
          </div>
          <div>
            <div className="font-semibold mb-1">4. Save & Test</div>
            <ul className="list-disc pl-5 space-y-0.5">
              <li>Click <strong>Save Settings</strong> below.</li>
              <li>In Salestrail, click <strong>Test Connection</strong> (or make a test call). You should see a ✓ and the event will appear in the <strong>Last Webhook</strong> section below.</li>
            </ul>
          </div>
          <div className="rounded bg-amber-100/70 border border-amber-200 p-2 text-amber-900 text-[11px]">
            <i className="fas fa-exclamation-triangle mr-1"></i>
            <strong>Tip:</strong> Salestrail only sends call records to your webhook — no inbound writes to Salestrail. If you don't see data flowing, check your Salestrail plan includes Push API (paid tier).
          </div>
        </div>
      </details>
      
      {/* Push API Configuration */}
      {settings.enabled && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-4">
          <div className="flex items-start gap-2">
            <i className="fas fa-info-circle text-blue-600 mt-1"></i>
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Configure in Salestrail App:</p>
              <ol className="list-decimal ml-4 space-y-1">
                <li>Open Salestrail Dashboard → Settings → Push API</li>
                <li>Enable Push API and paste the Webhook URL below</li>
                <li>Set the Username and Password (same as configured below)</li>
                <li>Save and test the connection</li>
              </ol>
            </div>
          </div>
          
          {/* Webhook URL */}
          <div className="space-y-2">
            <Label>Webhook URL <span className="text-red-500">*</span></Label>
            <div className="flex gap-2">
              <Input
                value={getWebhookUrl()}
                readOnly
                className="bg-white font-mono text-sm"
              />
              <Button
                variant="outline"
                size="icon"
                onClick={() => copyToClipboard(getWebhookUrl(), 'Webhook URL')}
              >
                <i className="fas fa-copy"></i>
              </Button>
            </div>
          </div>
          
          {/* Webhook Username */}
          <div className="space-y-2">
            <Label>Webhook Username</Label>
            <Input
              value={settings.webhook_username}
              onChange={(e) => setSettings({ ...settings, webhook_username: e.target.value })}
              placeholder="patrickscrm"
            />
          </div>
          
          {/* Webhook Password */}
          <div className="space-y-2">
            <Label>Webhook Password</Label>
            <div className="flex gap-2">
              <Input
                type={showWebhookPassword ? "text" : "password"}
                value={settings.webhook_password}
                onChange={(e) => setSettings({ ...settings, webhook_password: e.target.value })}
                placeholder="Enter password to set/update"
              />
              <Button variant="ghost" size="icon" onClick={() => setShowWebhookPassword(!showWebhookPassword)}>
                <i className={`fas fa-eye${showWebhookPassword ? '-slash' : ''}`}></i>
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Set a secure password. Use the same password in Salestrail Push API settings.
            </p>
          </div>
          
          {lastWebhook && (
            <div className="text-xs text-green-600 flex items-center gap-1">
              <i className="fas fa-check-circle"></i>
              Last webhook received: {new Date(lastWebhook).toLocaleString()}
            </div>
          )}
        </div>
      )}
      
      <div className="flex flex-wrap gap-2">
        <Button onClick={handleSave} disabled={saving} size="sm">
          {saving ? <><i className="fas fa-spinner fa-spin mr-2"></i>Saving...</> : <><i className="fas fa-save mr-2"></i>Save</>}
        </Button>
      </div>
    </div>
  );
};

// Admin diagnostic: which counsellors actually have an active push subscription
const PushHealthPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [broadcasting, setBroadcasting] = useState(false);

  const load = async () => {
    try {
      const res = await apiClient.get('/notifications/push-health');
      setData(res.data);
    } catch { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await apiClient.get('/notifications/push-health');
        if (active) setData(res.data);
      } catch { /* ignore */ }
      if (active) setLoading(false);
    })();
    return () => { active = false; };
  }, []);

  const broadcast = async (targetId, name) => {
    setBroadcasting(true);
    try {
      const res = await apiClient.post('/notifications/test-broadcast', targetId ? { target_id: targetId } : {});
      const { total_delivered, targets } = res.data;
      if (total_delivered > 0) toast.success(`Test push delivered to ${total_delivered} device(s) across ${targets} counsellor(s)`);
      else toast.warning(name ? `${name} has no active devices subscribed` : 'No counsellors have active subscriptions yet');
    } catch { toast.error('Failed to send test push'); }
    finally { setBroadcasting(false); }
  };

  if (loading) return null;
  if (!data) return null;

  return (
    <Card data-testid="push-health-panel">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <i className="fas fa-heart-pulse text-rose-600"></i> Counsellor Push Health
        </CardTitle>
        <CardDescription>See which counsellors actually have a device subscribed, and send a real test push to verify delivery.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-3 gap-2">
          <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-center" data-testid="push-health-subscribed">
            <div className="text-2xl font-bold text-emerald-700">{data.subscribed_counsellors}</div>
            <div className="text-xs text-emerald-700">Subscribed</div>
          </div>
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-center" data-testid="push-health-unsubscribed">
            <div className="text-2xl font-bold text-amber-700">{data.unsubscribed_counsellors}</div>
            <div className="text-xs text-amber-700">Not Subscribed</div>
          </div>
          <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-center">
            <div className="text-2xl font-bold text-blue-700">{data.total_subscriptions}</div>
            <div className="text-xs text-blue-700">Total Devices</div>
          </div>
        </div>

        {!data.vapid_configured && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-testid="push-health-vapid-warning">
            <i className="fas fa-triangle-exclamation mr-1.5"></i>VAPID keys are NOT configured on this server — push delivery will fail for everyone.
          </div>
        )}

        <Button size="sm" onClick={() => broadcast(null, null)} disabled={broadcasting} data-testid="broadcast-test-all-btn">
          {broadcasting ? <><i className="fas fa-spinner fa-spin mr-1.5"></i>Sending...</> : <><i className="fas fa-bullhorn mr-1.5"></i>Send Test Push to All Counsellors</>}
        </Button>

        <div className="divide-y border rounded-lg">
          {data.counsellors.map((c) => (
            <div key={c.id} className="flex items-center justify-between px-3 py-2" data-testid={`push-health-row-${c.id}`}>
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{c.full_name}</div>
                <div className="text-xs text-gray-500 truncate">{c.email}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {c.device_count > 0 ? (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">{c.device_count} device{c.device_count > 1 ? 's' : ''}</span>
                ) : (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">no device</span>
                )}
                <Button variant="outline" size="sm" className="h-7 px-2" disabled={broadcasting || c.device_count === 0}
                  onClick={() => broadcast(c.id, c.full_name)} data-testid={`broadcast-test-${c.id}`}>
                  <i className="fas fa-paper-plane"></i>
                </Button>
              </div>
            </div>
          ))}
        </div>
        <Button variant="ghost" size="sm" onClick={() => { setLoading(true); load(); }} data-testid="push-health-refresh-btn">
          <i className="fas fa-rotate mr-1.5"></i>Refresh
        </Button>
      </CardContent>
    </Card>
  );
};

// Notification Preferences Component
const NotificationPreferences = ({ user }) => {
  const { prefs, loading, loadError, savingKey, toggle: togglePref, retry } = useNotificationPreferences(`${user?.id}:${user?.role}`);
  const [pushStatus, setPushStatus] = useState({ isSupported: false, permission: 'default', isSubscribed: false, deviceType: 'web', deviceName: 'Unknown' });
  const [testingPush, setTestingPush] = useState(false);

  useEffect(() => {
    const init = async () => {
      try {
        const status = await notificationService.getStatus();
        setPushStatus(status);
      } catch { /* ignore */ }
    };
    init();
  }, []);

  const counselorPrefs = [
    { key: 'new_lead_assignment', label: 'Lead assignments', desc: 'Leads assigned or reassigned to you.', icon: 'fa-user-plus', color: 'text-blue-600' },
    { key: 'counselling_updates', label: 'Counselling sessions', desc: 'Bookings, schedule changes, cancellations and student attendance replies.', icon: 'fa-calendar-check', color: 'text-teal-600' },
    { key: 'follow_up_reminder', label: 'Tasks & follow-ups', desc: 'Assigned tasks, due reminders and task summaries.', icon: 'fa-clock', color: 'text-amber-600' },
    { key: 'lead_recontact', label: 'Student replies & re-contact', desc: 'WhatsApp, portal messages and repeat enquiries from your students.', icon: 'fa-comments', color: 'text-sky-600' },
    { key: 'admission_stage_update', label: 'Admission updates', desc: 'Changes to your students’ admission stages by other staff.', icon: 'fa-graduation-cap', color: 'text-green-600' },
    { key: 'payment_received', label: 'Student payment received', desc: 'Student fee receipts and registration-payment verification.', icon: 'fa-receipt', color: 'text-emerald-600' },
    { key: 'incentive_earned', label: 'My incentives & bonuses earned', desc: 'Your eligible base incentives and awarded bonuses.', icon: 'fa-trophy', color: 'text-amber-600' },
    { key: 'incentive_paid', label: 'My incentives & bonuses paid', desc: 'Accounts records payment of your incentive or bonus.', icon: 'fa-wallet', color: 'text-teal-600' },
  ];

  const adminPrefs = [
    { key: 'counselor_availability_change', label: 'Counselor Availability Change', desc: 'When a counselor marks themselves available or unavailable', icon: 'fa-user-clock', color: 'text-indigo-600' },
    { key: 'new_lead_created', label: 'New Lead Created', desc: 'When any new lead enters the system (WATI or manual)', icon: 'fa-plus-circle', color: 'text-blue-600' },
    { key: 'invoice_raised', label: 'Invoice Raised', desc: 'When a new invoice is generated', icon: 'fa-file-invoice', color: 'text-amber-600' },
    { key: 'payment_received', label: 'Payment Received', desc: 'When any payment is marked as received', icon: 'fa-rupee-sign', color: 'text-emerald-600' },
    { key: 'admission_created', label: 'Admission Created', desc: 'When a new admission entry is created', icon: 'fa-graduation-cap', color: 'text-purple-600' },
    { key: 'lead_reassigned', label: 'Lead Reassigned', desc: 'When a lead is moved between counselors', icon: 'fa-exchange-alt', color: 'text-orange-600' },
    { key: 'backup_complete', label: 'Backup Complete', desc: 'When a system backup finishes successfully', icon: 'fa-database', color: 'text-gray-600' },
  ];

  const documentationPrefs = [
    { key: 'document_uploaded', label: 'Document Uploaded', desc: 'When a student uploads a new document for review', icon: 'fa-file-upload', color: 'text-blue-600' },
    { key: 'document_status_changed', label: 'Document Verified / Rejected', desc: 'When a document review status changes', icon: 'fa-file-check', color: 'text-green-600' },
    { key: 'admission_stage_update', label: 'Admission Stage Update', desc: 'When an admission moves to a stage requiring documents', icon: 'fa-tasks', color: 'text-indigo-600' },
    { key: 'admission_created', label: 'New Admission Created', desc: 'When a new admission is created that needs document collection', icon: 'fa-graduation-cap', color: 'text-purple-600' },
  ];

  const accountantPrefs = [
    { key: 'payment_received', label: 'Payment Received', desc: 'When any payment is marked as received', icon: 'fa-rupee-sign', color: 'text-emerald-600' },
    { key: 'invoice_raised', label: 'Invoice Raised', desc: 'When a new invoice is generated', icon: 'fa-file-invoice', color: 'text-amber-600' },
    { key: 'payment_overdue', label: 'Payment Overdue', desc: 'When a scheduled payment is past due date', icon: 'fa-exclamation-triangle', color: 'text-red-600' },
    { key: 'admission_fee_pending', label: 'Admission Fee Pending', desc: 'When a new admission requires fee collection', icon: 'fa-hand-holding-usd', color: 'text-orange-600' },
    { key: 'admission_created', label: 'New Admission Created', desc: 'When a new admission entry is created', icon: 'fa-graduation-cap', color: 'text-purple-600' },
  ];

  const operatingHeadPrefs = [
    { key: 'counselor_availability_change', label: 'Counselor Availability Change', desc: 'When a counselor marks themselves available or unavailable', icon: 'fa-user-clock', color: 'text-indigo-600' },
    { key: 'new_lead_created', label: 'New Lead Created', desc: 'When any new lead enters the system', icon: 'fa-plus-circle', color: 'text-blue-600' },
    { key: 'admission_created', label: 'Admission Created', desc: 'When a new admission entry is created', icon: 'fa-graduation-cap', color: 'text-purple-600' },
    { key: 'payment_received', label: 'Payment Received', desc: 'When any payment is marked as received', icon: 'fa-rupee-sign', color: 'text-emerald-600' },
    { key: 'lead_reassigned', label: 'Lead Reassigned', desc: 'When a lead is moved between counselors', icon: 'fa-exchange-alt', color: 'text-orange-600' },
    { key: 'invoice_raised', label: 'Invoice Raised', desc: 'When a new invoice is generated', icon: 'fa-file-invoice', color: 'text-amber-600' },
  ];

  const prefMap = {
    admin: { list: adminPrefs, title: 'Admin Notifications' },
    counselor: { list: counselorPrefs, title: 'Counsellor notifications' },
    team_leader: { list: counselorPrefs, title: 'Counsellor notifications' },
    documentation_team: { list: documentationPrefs, title: 'Documentation Notifications' },
    accountant: { list: accountantPrefs, title: 'Finance Notifications' },
    operating_head: { list: operatingHeadPrefs, title: 'Operations Notifications' },
  };
  const { list: activePrefList, title: sectionTitle } = prefMap[user?.role] || prefMap.counselor;

  if (loading) return (
    <Card data-testid="notification-preferences-loading"><CardContent className="p-8 text-center"><i className="fas fa-spinner fa-spin text-2xl text-gray-400"></i><p className="text-gray-500 mt-2">Loading preferences...</p></CardContent></Card>
  );
  if (loadError) return <div role="alert" className="space-y-3 p-4" data-testid="notification-preferences-error"><p data-testid="notification-preferences-error-message">Could not load your notification preferences.</p><Button variant="outline" onClick={retry} data-testid="notification-preferences-retry">Retry</Button></div>;

  return (
    <div className="space-y-4" data-testid="notification-preferences">
      {/* Push Notification Master Toggle */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 shrink-0 bg-indigo-100 rounded-lg flex items-center justify-center">
                <i className="fas fa-bell text-indigo-600 text-lg"></i>
              </div>
              <div className="min-w-0">
                <Label className="text-base font-semibold" data-testid="push-notifications-label">Push Notifications</Label>
                <p className="text-sm text-gray-500 mt-0.5 break-words" data-testid="push-notifications-status">
                  {!pushStatus.isSupported ? 'Not supported in this browser' :
                   pushStatus.permission === 'denied' ? 'Blocked — enable in browser settings' :
                   pushStatus.isSubscribed ? `Active on ${pushStatus.deviceName}` : 'Enable to receive real-time alerts'}
                </p>
              </div>
            </div>
            {pushStatus.isSupported && pushStatus.permission !== 'denied' && (
              <Switch
                data-testid="push-notifications-toggle"
                className="shrink-0"
                aria-label="Push notifications on this device"
                checked={pushStatus.isSubscribed}
                onCheckedChange={async (checked) => {
                  try {
                    if (checked) {
                      await notificationService.subscribe();
                      toast.success('Push notifications enabled!');
                    } else {
                      await notificationService.unsubscribe();
                      toast.success('Push notifications disabled');
                    }
                    const status = await notificationService.getStatus();
                    setPushStatus(status);
                  } catch (error) {
                    toast.error(checked ? 'Failed to enable notifications' : 'Failed to disable notifications');
                  }
                }}
              />
            )}
          </div>
          {pushStatus.isSubscribed && (
            <div className="mt-3 ml-[52px]">
              <Button
                variant="outline"
                size="sm"
                data-testid="send-test-notification-btn"
                onClick={async () => {
                  setTestingPush(true);
                  try {
                    const result = await notificationService.sendTestNotification();
                    if (result.status === 'success') toast.success(`Test sent to ${result.devices_notified} device(s)`);
                    else toast.warning('No active subscriptions found');
                  } catch { toast.error('Failed to send test'); }
                  finally { setTestingPush(false); }
                }}
                disabled={testingPush}
              >
                {testingPush ? <><i className="fas fa-spinner fa-spin mr-1.5"></i>Sending...</> : <><i className="fas fa-paper-plane mr-1.5"></i>Send Test</>}
              </Button>
            </div>
          )}
          {pushStatus.isSupported && pushStatus.permission === 'denied' && (
            <div className="mt-3 ml-[52px] p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              <p className="font-medium">Notifications are blocked in your browser.</p>
              <p className="mt-1">Click the lock icon in the address bar → Site Settings → Allow Notifications → Refresh.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Notification Type Preferences */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base" data-testid="notification-preferences-title">
            {sectionTitle}
          </CardTitle>
          <CardDescription data-testid="notification-preferences-scope">New in-app alerts and push notifications. Existing history stays available.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-1">
          {activePrefList.map(({ key, label, desc, icon, color }) => (
            <div key={key} className="flex items-center justify-between gap-3 py-3 min-h-[64px] border-b last:border-0" data-testid={`notif-pref-${key}`}>
              <div className="flex items-center gap-3 min-w-0">
                <i className={`fas ${icon} ${color} w-5 shrink-0 text-center`} aria-hidden="true"></i>
                <div className="min-w-0">
                  <Label htmlFor={`notification-${key}`} className="text-sm font-medium break-words" data-testid={`notif-label-${key}`}>{label}</Label>
                  <p className="text-xs text-gray-500 break-words" data-testid={`notif-description-${key}`}>{desc}</p>
                </div>
              </div>
              <Switch
                id={`notification-${key}`}
                className="shrink-0"
                aria-label={label}
                disabled={!!savingKey}
                checked={prefs[key] !== false}
                onCheckedChange={checked => togglePref(key, checked)}
                data-testid={`notif-toggle-${key}`}
              />
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Admin-only push delivery diagnostic */}
      {(user?.role === 'admin' || user?.role === 'operating_head') && <PushHealthPanel />}
    </div>
  );
};

// Mobile Settings Navigation - Dropdown select
const MobileSettingsNav = ({ user, activeTab, onTabChange }) => {
  const allTabs = [
    { value: 'general', label: 'General', icon: 'fa-building', adminOnly: true },
    { value: 'leads', label: 'Lead Management', icon: 'fa-user-plus', adminOnly: true },
    { value: 'intakes', label: 'Intakes', icon: 'fa-calendar-alt', adminOnly: true },
    { value: 'incentives', label: 'Incentives', icon: 'fa-trophy', adminOnly: true },
    { value: 'finance', label: 'Finance', icon: 'fa-file-invoice-dollar', roles: ['admin', 'accountant'] },
    { value: 'integrations', label: 'Integrations', icon: 'fa-plug', roles: ['admin', 'operating_head'] },
    { value: 'notifications', label: 'Notifications', icon: 'fa-bell', roles: null },
    { value: 'catalog', label: 'Catalog', icon: 'fa-book', adminOnly: true },
    { value: 'agreements', label: 'Agreements', icon: 'fa-file-signature', adminOnly: true },
    { value: 'payment-schedules', label: 'Payment Schedules', icon: 'fa-rupee-sign', adminOnly: true },
    { value: 'backup', label: 'Backup', icon: 'fa-database', adminOnly: true },
  ];

  const visibleTabs = allTabs.filter(t => {
    if (t.roles) return t.roles.includes(user?.role);
    if (t.adminOnly) return user?.role === 'admin';
    return true;
  });

  return (
    <select
      value={activeTab}
      onChange={(e) => onTabChange(e.target.value)}
      data-testid="mobile-settings-nav"
      className="w-full px-3 py-2.5 text-sm font-medium bg-white border border-gray-200 rounded-lg text-[#192650] focus:outline-none focus:ring-2 focus:ring-[#192650]/20"
    >
      {visibleTabs.map(t => (
        <option key={t.value} value={t.value}>{t.label}</option>
      ))}
    </select>
  );
};

// Collapsible shell used in Settings → Integrations to keep the page compact.
// Renders a clickable header (icon + name + optional status badge + chevron) and
// shows `children` only when `open` is true. Only one integration is open at a
// time (controlled by the parent), so the page never gets long.
const IntegrationCollapsible = ({ id, name, subtitle, iconClass, iconBg, badge, open, onToggle, children }) => (
  <Card className="overflow-hidden">
    <button
      type="button"
      onClick={() => onToggle(id)}
      data-testid={`integration-toggle-${id}`}
      className="w-full text-left px-4 py-3 flex items-center justify-between gap-3 hover:bg-slate-50 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500/40"
      aria-expanded={open}
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${iconBg || 'bg-slate-100'}`}>
          <i className={`${iconClass} text-lg`}></i>
        </div>
        <div className="min-w-0">
          <div className="font-medium text-slate-900 text-sm truncate">{name}</div>
          {subtitle && <div className="text-xs text-slate-500 truncate">{subtitle}</div>}
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {badge}
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </div>
    </button>
    {open && (
      <div className="px-4 pb-4 pt-2 border-t border-slate-100 space-y-4">
        {children}
      </div>
    )}
  </Card>
);

const Settings = () => {
  const { user } = useAuth();
  const [integrationSettings, setIntegrationSettings] = useState({
    wati_api_endpoint: '',
    wati_access_token: '',
    razorpay_key_id: '',
    razorpay_key_secret: '',
    razorpay_webhook_secret: '',
    meta_app_secret: '',
    meta_verify_token: '',
    notifications_enabled: true
  });

  const [saving, setSaving] = useState(false);
  
  // Active tab state (controlled for mobile dropdown)
  const defaultTab = user?.role === 'admin' ? 'general' : (user?.role === 'accountant' ? 'finance' : 'notifications');
  const [activeTab, setActiveTab] = useState(defaultTab);

  // Integrations accordion — only one open at a time so the page stays compact.
  const [openIntegration, setOpenIntegration] = useState(null);
  const toggleIntegration = (id) => setOpenIntegration((cur) => (cur === id ? null : id));

  // WATI API config state
  const [watiConfig, setWatiConfig] = useState({
    patrick_endpoint: '',
    patrick_token: '',
    gradbud_endpoint: '',
    gradbud_token: '',
    patrick_token_masked: '',
    gradbud_token_masked: '',
    patrick_token_set: false,
    gradbud_token_set: false,
  });
  const [watiConfigLoading, setWatiConfigLoading] = useState(true);
  const [watiTestingPatrick, setWatiTestingPatrick] = useState(false);
  const [watiTestingGradbud, setWatiTestingGradbud] = useState(false);
  const [savingWati, setSavingWati] = useState(false);

  // Organization Settings state
  const [orgSettings, setOrgSettings] = useState({ org_name: '', org_email: '', org_address: '', org_phone: '' });
  const [savingOrg, setSavingOrg] = useState(false);

  // Student Portal Access toggle
  const [studentPortalEnabled, setStudentPortalEnabled] = useState(true);
  const [studentPortalMessage, setStudentPortalMessage] = useState('');
  const [savingStudentPortal, setSavingStudentPortal] = useState(false);
  const [loadingStudentPortal, setLoadingStudentPortal] = useState(false);

  // RNR Threshold state
  const [rnrThreshold, setRnrThreshold] = useState(5);
  const [savingRnrThreshold, setSavingRnrThreshold] = useState(false);
  
  // Intake Periods State
  const [intakePeriods, setIntakePeriods] = useState([]);
  const [loadingIntakes, setLoadingIntakes] = useState(true);
  const [showAddIntakeDialog, setShowAddIntakeDialog] = useState(false);
  const [editingIntake, setEditingIntake] = useState(null);
  const [newIntake, setNewIntake] = useState({ name: '', start_date: '', end_date: '', status: 'Active' });
  const [savingIntake, setSavingIntake] = useState(false);
  
  // WATI Counselor Assignments
  const [counselors, setCounselors] = useState([]);
  const [patrickCounselors, setPatrickCounselors] = useState([]);
  const [gradbudCounselors, setGradbudCounselors] = useState([]);
  const [loadingCounselors, setLoadingCounselors] = useState(true);
  
  // Backup & Restore State
  const [backups, setBackups] = useState([]);
  const [loadingBackups, setLoadingBackups] = useState(false);
  const [creatingBackup, setCreatingBackup] = useState(false);
  const [backupInProgress, setBackupInProgress] = useState(false);
  const [newBackupName, setNewBackupName] = useState(null);
  const [importingBackup, setImportingBackup] = useState(false);
  
  // Incentive Settings State
  const [incentiveSettings, setIncentiveSettings] = useState({
    is_enabled: true,
    mbbs_commission: 10000,
    other_commission: 5000,
    auto_assign_leads: true,
    power_hours_enabled: false,
    // Day-specific schedules
    power_hours_sunday_start: '10:00',
    power_hours_sunday_end: '22:00',
    power_hours_weekday_start: '19:00',
    power_hours_weekday_end: '22:00',
    power_hours_bonus_percentage: 50,
    // Legacy fields (kept for backwards compatibility)
    power_hours_start: '19:00',
    power_hours_end: '22:00',
    weekly_bonus_enabled: true,
    weekly_min_admissions: 3,
    weekly_bonus_amount: 2000,
    monthly_champion_enabled: true,
    monthly_champion_min_admissions: 5,
    monthly_champion_bonus: 2000,
    first_strike_enabled: true,
    first_strike_window_minutes: 15,
    first_strike_min_calls: 30,
    first_strike_bonus: 2000,
    team_bonus_enabled: true,
    team_target_multiplier: 5,
    team_bonus_extra_threshold: 5,
    team_member_min_admissions: 5,
    team_bonus_amount: 2000,
    intake_champion_enabled: true,
    intake_champion_min_admissions: 25,
    intake_champion_bonus: 25000
  });
  const [loadingIncentives, setLoadingIncentives] = useState(true);
  const [savingIncentives, setSavingIncentives] = useState(false);
  
  // Fetch Incentive Settings
  const fetchIncentiveSettings = async () => {
    try {
      setLoadingIncentives(true);
      const response = await apiClient.get('/incentive/settings');
      setIncentiveSettings(response.data);
    } catch (error) {
      console.error('Error fetching incentive settings:', error);
    } finally {
      setLoadingIncentives(false);
    }
  };
  
  // Save Incentive Settings
  const handleSaveIncentiveSettings = async () => {
    try {
      setSavingIncentives(true);
      await apiClient.put('/incentive/settings', incentiveSettings);
      toast.success('Incentive settings saved successfully!');
    } catch (error) {
      console.error('Error saving incentive settings:', error);
      toast.error('Failed to save incentive settings');
    } finally {
      setSavingIncentives(false);
    }
  };
  
  // Fetch incentive settings on mount (for admin)
  useEffect(() => {
    if (user?.role === 'admin') {
      fetchIncentiveSettings();
    }
  }, [user]);
  
  // Fetch Backups
  const fetchBackups = async () => {
    try {
      setLoadingBackups(true);
      const response = await apiClient.get('/backup/list');
      setBackups(response.data.backups || []);
    } catch (error) {
      console.error('Error fetching backups:', error);
      toast.error('Failed to load backups');
    } finally {
      setLoadingBackups(false);
    }
  };

  // Polls the backup list until the newly-started backup appears completed,
  // then flips the "in progress" chip and briefly highlights the fresh entry.
  const pollForNewBackup = async (beforeNames) => {
    for (let i = 0; i < 20; i++) {
      await new Promise((r) => setTimeout(r, 3000));
      try {
        const list = await apiClient.get('/backup/list');
        const items = list.data.backups || [];
        setBackups(items);
        const fresh = items.find((b) => !beforeNames.has(b.backup_name) && b.status === 'completed');
        if (fresh) {
          setBackupInProgress(false);
          setNewBackupName(fresh.backup_name);
          const docs = fresh.total_documents?.toLocaleString?.() || fresh.total_documents || 0;
          toast.success(`Backup completed — ${docs} documents saved.`);
          setTimeout(() => setNewBackupName(null), 8000);
          return;
        }
      } catch (e) { /* transient — keep polling */ }
    }
    // Fell through without seeing it complete — stop the chip, keep the list fresh.
    setBackupInProgress(false);
    fetchBackups();
    toast.info('Backup is taking longer than usual — it will appear once done.');
  };

  const createBackup = async () => {
    try {
      setCreatingBackup(true);
      const beforeNames = new Set(backups.map((b) => b.backup_name));
      const res = await apiClient.post('/backup/create');
      if (res.data?.status === 'started') {
        setBackupInProgress(true);
        toast.success(res.data.message || 'Backup started — running in the background.');
        pollForNewBackup(beforeNames); // detached; chip tracks progress
      } else if (res.data?.backup?.status === 'completed') {
        toast.success('Backup created successfully!');
        fetchBackups();
      } else {
        toast.warning('Backup completed with warnings');
        fetchBackups();
      }
    } catch (error) {
      setBackupInProgress(false);
      console.error('Error creating backup:', error);
      toast.error(error.response?.data?.detail || 'Failed to create backup');
    } finally {
      setCreatingBackup(false);
    }
  };

  const restoreBackup = async (backupName) => {
    const confirmed = window.confirm(
      `⚠️ WARNING: This will replace ALL current data with backup data!\n\nBackup: ${backupName}\n\nAre you absolutely sure you want to proceed?`
    );
    if (!confirmed) return;

    const doubleConfirmed = window.confirm(
      `🚨 FINAL CONFIRMATION\n\nThis action is IRREVERSIBLE!\nAll current students, payments, and data will be replaced.\n\nClick OK to confirm.`
    );
    if (!doubleConfirmed) return;

    try {
      setLoadingBackups(true);
      await apiClient.post(`/backup/restore/${backupName}`);
      toast.success('Database restored successfully! Please refresh the page.');
      setTimeout(() => window.location.reload(), 2000);
    } catch (error) {
      console.error('Error restoring backup:', error);
      toast.error(error.response?.data?.detail || 'Failed to restore backup');
    } finally {
      setLoadingBackups(false);
    }
  };

  const downloadBackup = async (backupName) => {
    try {
      toast.info('Preparing download...');
      const response = await apiClient.get(`/backup/download/${backupName}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${backupName}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Backup downloaded successfully!');
    } catch (error) {
      console.error('Error downloading backup:', error);
      toast.error(error.response?.data?.detail || 'Failed to download backup');
    }
  };

  const handleImportBackup = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.name.endsWith('.json')) {
      toast.error('Please select a valid JSON backup file');
      event.target.value = '';
      return;
    }

    // Confirm import
    const confirmed = window.confirm(
      `⚠️ WARNING: Importing backup will REPLACE all current data!\n\nFile: ${file.name}\n\nAre you absolutely sure?`
    );
    if (!confirmed) {
      event.target.value = '';
      return;
    }

    const doubleConfirmed = window.confirm(
      `🚨 FINAL CONFIRMATION\n\nThis action is IRREVERSIBLE!\nAll current students, payments, and data will be replaced.\n\nClick OK to confirm.`
    );
    if (!doubleConfirmed) {
      event.target.value = '';
      return;
    }

    try {
      setImportingBackup(true);
      toast.info('Importing backup... This may take a moment.');

      const formData = new FormData();
      formData.append('file', file);

      const response = await apiClient.post('/backup/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success(`Backup imported successfully! ${response.data.restore_stats?.documents_restored || 0} documents restored.`);
      
      // Refresh backups list
      fetchBackups();
      
      // Suggest page refresh
      setTimeout(() => {
        if (window.confirm('Backup restored! Would you like to refresh the page to see the updated data?')) {
          window.location.reload();
        }
      }, 1000);

    } catch (error) {
      console.error('Error importing backup:', error);
      toast.error(error.response?.data?.detail || 'Failed to import backup');
    } finally {
      setImportingBackup(false);
      event.target.value = ''; // Reset file input
    }
  };

  const formatBackupDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };
  
  // Fetch Intake Periods
  const fetchIntakePeriods = async () => {
    try {
      setLoadingIntakes(true);
      const response = await apiClient.get('/system-settings/intakes');
      setIntakePeriods(response.data.periods || []);
    } catch (error) {
      console.error('Failed to fetch intake periods:', error);
    } finally {
      setLoadingIntakes(false);
    }
  };
  
  // Add Intake Period
  const addIntakePeriod = async () => {
    if (!newIntake.name.trim()) {
      toast.error('Intake name is required');
      return;
    }
    try {
      setSavingIntake(true);
      await apiClient.post('/system-settings/intakes', newIntake);
      toast.success('Intake period added');
      setShowAddIntakeDialog(false);
      setNewIntake({ name: '', start_date: '', end_date: '', status: 'Active' });
      fetchIntakePeriods();
    } catch (error) {
      console.error('Failed to add intake:', error);
      toast.error(error.response?.data?.detail || 'Failed to add intake period');
    } finally {
      setSavingIntake(false);
    }
  };
  
  // Update Intake Period
  const updateIntakePeriod = async () => {
    if (!editingIntake?.name?.trim()) {
      toast.error('Intake name is required');
      return;
    }
    try {
      setSavingIntake(true);
      await apiClient.put(`/system-settings/intakes/${editingIntake.id}`, editingIntake);
      toast.success('Intake period updated');
      setEditingIntake(null);
      fetchIntakePeriods();
    } catch (error) {
      console.error('Failed to update intake:', error);
      toast.error(error.response?.data?.detail || 'Failed to update intake period');
    } finally {
      setSavingIntake(false);
    }
  };
  
  // Delete Intake Period
  const deleteIntakePeriod = async (intakeId) => {
    if (!window.confirm('Are you sure you want to delete this intake period?')) return;
    try {
      await apiClient.delete(`/system-settings/intakes/${intakeId}`);
      toast.success('Intake period deleted');
      fetchIntakePeriods();
    } catch (error) {
      console.error('Failed to delete intake:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete intake period');
    }
  };
  
  // Update Intake Status (Active/Upcoming/Closed)
  const updateIntakeStatus = async (intake, newStatus) => {
    try {
      await apiClient.put(`/system-settings/intakes/${intake.id}`, { ...intake, status: newStatus });
      toast.success(`Intake marked as ${newStatus}`);
      fetchIntakePeriods();
    } catch (error) {
      console.error('Failed to update intake status:', error);
      toast.error('Failed to update intake status');
    }
  };

  const fetchWATICounselors = async () => {
    try {
      setLoadingCounselors(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/wati/counselor-assignments`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCounselors(data.counselors || []);
        setPatrickCounselors(data.patrick_assigned || []);
        setGradbudCounselors(data.gradbud_assigned || []);
      }
    } catch (error) {
      console.error('Failed to fetch WATI counselor assignments:', error);
    } finally {
      setLoadingCounselors(false);
    }
  };

  // Fetch push notification status on mount (unused - now in NotificationPreferences)
  // Fetch WATI counselor assignments on mount
  useEffect(() => {
    if (user?.role === 'admin' || user?.role === 'operating_head') {
      fetchWATICounselors();
    }
  }, [user]);
  
  // Fetch Intakes on mount (Admin only)
  useEffect(() => {
    if (user?.role === 'admin') {
      fetchIntakePeriods();
      fetchBackups();
    }
  }, [user]);

  const handleSaveCounselorAssignment = async (account) => {
    try {
      setSaving(true);
      const counselorIds = account === 'patrick' ? patrickCounselors : gradbudCounselors;
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/wati/counselor-assignments`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          account: account,
          counselor_ids: counselorIds
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`${account === 'patrick' ? "Patrick's" : "Gradbud's"} counselor assignments saved! (${data.counselor_count} counselors)`);
      } else {
        toast.error('Failed to save counselor assignments');
      }
    } catch (error) {
      toast.error('Error saving counselor assignments');
    } finally {
      setSaving(false);
    }
  };

  const toggleCounselor = (counselorId, account) => {
    if (account === 'patrick') {
      setPatrickCounselors(prev => 
        prev.includes(counselorId) 
          ? prev.filter(id => id !== counselorId)
          : [...prev, counselorId]
      );
    } else {
      setGradbudCounselors(prev => 
        prev.includes(counselorId) 
          ? prev.filter(id => id !== counselorId)
          : [...prev, counselorId]
      );
    }
  };

  const selectAllCounselors = (account) => {
    const allIds = counselors.map(c => c.id);
    if (account === 'patrick') {
      setPatrickCounselors(allIds);
    } else {
      setGradbudCounselors(allIds);
    }
  };

  const clearAllCounselors = (account) => {
    if (account === 'patrick') {
      setPatrickCounselors([]);
    } else {
      setGradbudCounselors([]);
    }
  };

  const handleSaveIntegrations = async () => {
    setSaving(true);
    try {
      toast.success('Integration settings saved successfully!');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // Load WATI API config on mount
  useEffect(() => {
    const loadWatiConfig = async () => {
      try {
        const resp = await apiClient.get('/wati/settings');
        setWatiConfig(prev => ({
          ...prev,
          patrick_endpoint: resp.data.patrick_endpoint || '',
          patrick_token_masked: resp.data.patrick_token_masked || '',
          patrick_token_set: resp.data.patrick_token_set || false,
          gradbud_endpoint: resp.data.gradbud_endpoint || '',
          gradbud_token_masked: resp.data.gradbud_token_masked || '',
          gradbud_token_set: resp.data.gradbud_token_set || false,
        }));
      } catch (err) {
        console.error('Failed to load WATI config:', err);
      } finally {
        setWatiConfigLoading(false);
      }
    };
    if (user?.role === 'admin') loadWatiConfig();
    else setWatiConfigLoading(false);
  }, [user]);

  const handleSaveWatiConfig = async (account) => {
    setSavingWati(true);
    try {
      const payload = {};
      if (account === 'patrick') {
        if (watiConfig.patrick_endpoint) payload.patrick_endpoint = watiConfig.patrick_endpoint;
        if (watiConfig.patrick_token) payload.patrick_token = watiConfig.patrick_token;
      } else {
        if (watiConfig.gradbud_endpoint) payload.gradbud_endpoint = watiConfig.gradbud_endpoint;
        if (watiConfig.gradbud_token) payload.gradbud_token = watiConfig.gradbud_token;
      }
      await apiClient.put('/wati/settings', payload);
      toast.success(`WATI ${account} API settings saved!`);
      // Reload to get masked token
      const resp = await apiClient.get('/wati/settings');
      setWatiConfig(prev => ({
        ...prev,
        patrick_endpoint: resp.data.patrick_endpoint || '',
        patrick_token: '',
        patrick_token_masked: resp.data.patrick_token_masked || '',
        patrick_token_set: resp.data.patrick_token_set || false,
        gradbud_endpoint: resp.data.gradbud_endpoint || '',
        gradbud_token: '',
        gradbud_token_masked: resp.data.gradbud_token_masked || '',
        gradbud_token_set: resp.data.gradbud_token_set || false,
      }));
    } catch (err) {
      toast.error('Failed to save WATI settings');
    } finally {
      setSavingWati(false);
    }
  };

  const handleTestWatiConnection = async (account) => {
    const setTesting = account === 'patrick' ? setWatiTestingPatrick : setWatiTestingGradbud;
    setTesting(true);
    try {
      const resp = await apiClient.post('/wati/test-connection', { account });
      if (resp.data.success) {
        toast.success(`WATI ${account} connection successful!`);
      } else {
        toast.error(`WATI ${account} connection failed: ${resp.data.error}`);
      }
    } catch (err) {
      toast.error('Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  // Load RNR threshold on mount
  useEffect(() => {
    const loadRnrThreshold = async () => {
      try {
        const resp = await apiClient.get('/settings/rnr-threshold');
        setRnrThreshold(resp.data.rnr_threshold || 5);
      } catch (err) {
        console.error('Failed to load RNR threshold:', err);
      }
    };
    if (user?.role === 'admin') loadRnrThreshold();
  }, [user]);

  // Load Organization Settings on mount
  useEffect(() => {
    const loadOrgSettings = async () => {
      try {
        const resp = await apiClient.get('/settings/organization');
        setOrgSettings({
          org_name: resp.data.org_name || '',
          org_email: resp.data.org_email || '',
          org_address: resp.data.org_address || '',
          org_phone: resp.data.org_phone || '',
        });
      } catch (err) {
        console.error('Failed to load organization settings:', err);
      }
    };
    if (user?.role === 'admin') loadOrgSettings();
  }, [user]);

  const handleSaveOrgSettings = async () => {
    setSavingOrg(true);
    try {
      await apiClient.put('/settings/organization', orgSettings);
      toast.success('Organization settings saved');
    } catch (err) {
      toast.error('Failed to save organization settings');
    } finally {
      setSavingOrg(false);
    }
  };

  // Student Portal Access load/save
  useEffect(() => {
    const loadStudentPortalSettings = async () => {
      setLoadingStudentPortal(true);
      try {
        const resp = await apiClient.get('/settings/student-portal');
        setStudentPortalEnabled(resp.data.enabled !== false);
        setStudentPortalMessage(resp.data.disabled_message || '');
      } catch (err) {
        console.error('Failed to load student portal settings:', err);
      } finally {
        setLoadingStudentPortal(false);
      }
    };
    if (user?.role === 'admin') loadStudentPortalSettings();
  }, [user]);

  const handleToggleStudentPortal = async (enabled) => {
    setSavingStudentPortal(true);
    setStudentPortalEnabled(enabled);
    try {
      await apiClient.put('/settings/student-portal', {
        enabled,
        disabled_message: studentPortalMessage || 'Student portal access is currently unavailable. Please contact support.',
      });
      toast.success(enabled ? 'Student portal login enabled' : 'Student portal login disabled');
    } catch (err) {
      setStudentPortalEnabled(!enabled); // revert on error
      const detail = err.response?.data?.detail || err.message || 'Unknown error';
      toast.error(`Failed to update student portal: ${detail}`);
    } finally {
      setSavingStudentPortal(false);
    }
  };

  const handleSaveStudentPortalMessage = async () => {
    setSavingStudentPortal(true);
    try {
      await apiClient.put('/settings/student-portal', {
        enabled: studentPortalEnabled,
        disabled_message: studentPortalMessage,
      });
      toast.success('Message updated');
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Unknown error';
      toast.error(`Failed to save message: ${detail}`);
    } finally {
      setSavingStudentPortal(false);
    }
  };

  const handleSaveRnrThreshold = async () => {
    setSavingRnrThreshold(true);
    try {
      await apiClient.put('/settings/rnr-threshold', { rnr_threshold: rnrThreshold });
      toast.success(`RNR threshold updated to ${rnrThreshold} consecutive calls`);
    } catch (err) {
      toast.error('Failed to update RNR threshold');
    } finally {
      setSavingRnrThreshold(false);
    }
  };


  const testIntegration = async (type) => {
    try {
      toast.info(`Testing ${type} integration...`);
      setTimeout(() => {
        toast.success(`${type} integration test successful!`);
      }, 2000);
    } catch (error) {
      toast.error(`${type} integration test failed`);
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6 fade-in p-3 sm:p-6 min-h-screen" style={{ backgroundColor: COLORS.background }}>
      {/* Header - Consistent with other modules */}
      <div className="sticky top-0 z-10 rounded-xl p-3 sm:p-4 -mx-3 sm:-mx-6 -mt-3 sm:-mt-6 mb-4" style={{ backgroundColor: COLORS.primary }}>
        <div className="flex items-center gap-3 px-0 sm:px-2">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: COLORS.accent }}>
            <SettingsIcon className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-base sm:text-lg font-semibold text-white">Settings</h1>
            <p className="text-[10px] sm:text-xs text-white/70 hidden sm:block">Manage system settings, integrations, and backups</p>
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-0" orientation="vertical">
        <div className="flex flex-col md:flex-row gap-3 md:gap-5 min-h-[600px]">
          {/* Sidebar Navigation - Desktop */}
          <div className="w-52 shrink-0 hidden md:block">
            <TabsList className="flex flex-col h-auto bg-white border rounded-xl p-1.5 gap-0.5 sticky top-20">
              {user?.role === 'admin' && (
                <TabsTrigger value="general" data-testid="general-tab" className="w-full justify-start text-[13px] px-3 py-2 rounded-lg data-[state=active]:bg-[#192650] data-[state=active]:text-white">
                  <i className="fas fa-building mr-2.5 w-4 text-center text-xs"></i>
                  General
                </TabsTrigger>
              )}
              {user?.role === 'admin' && (
                <TabsTrigger value="leads" data-testid="leads-tab" className="w-full justify-start text-[13px] px-3 py-2 rounded-lg data-[state=active]:bg-[#192650] data-[state=active]:text-white">
                  <i className="fas fa-user-plus mr-2.5 w-4 text-center text-xs"></i>
                  Lead Management
                </TabsTrigger>
              )}
              {user?.role === 'admin' && (
                <TabsTrigger value="intakes" data-testid="intakes-tab" className="w-full justify-start text-[13px] px-3 py-2 rounded-lg data-[state=active]:bg-[#192650] data-[state=active]:text-white">
                  <i className="fas fa-calendar-alt mr-2.5 w-4 text-center text-xs"></i>
                  Intakes
                </TabsTrigger>
              )}
              {user?.role === 'admin' && (
                <TabsTrigger value="incentives" data-testid="incentives-tab" className="w-full justify-start text-[13px] px-3 py-2 rounded-lg data-[state=active]:bg-[#192650] data-[state=active]:text-white">
                  <i className="fas fa-trophy mr-2.5 w-4 text-center text-xs"></i>
                  Incentives
                </TabsTrigger>
              )}
              {(user?.role === 'admin' || user?.role === 'accountant') && (
                <TabsTrigger value="finance" data-testid="finance-tab" className="w-full justify-start text-[13px] px-3 py-2 rounded-lg data-[state=active]:bg-[#192650] data-[state=active]:text-white">
                  <i className="fas fa-file-invoice-dollar mr-2.5 w-4 text-center text-xs"></i>
                  Finance
                </TabsTrigger>
              )}
              {(user?.role === 'admin' || user?.role === 'operating_head') && (
                <TabsTrigger value="pilot-workflow" data-testid="pilot-workflow-tab" className="w-full justify-start text-[13px] px-3 py-2 rounded-lg data-[state=active]:bg-[#192650] data-[state=active]:text-white">
                  <i className="fas fa-headset mr-2.5 w-4 text-center text-xs"></i>
                  Telecaller Workflow
                </TabsTrigger>
              )}
              {(user?.role === 'admin' || user?.role === 'operating_head') && (
                <TabsTrigger value="integrations" data-testid="integrations-tab" className="w-full justify-start text-[13px] px-3 py-2 rounded-lg data-[state=active]:bg-[#192650] data-[state=active]:text-white">
                  <i className="fas fa-plug mr-2.5 w-4 text-center text-xs"></i>
                  Integrations
                </TabsTrigger>
              )}
              <TabsTrigger value="notifications" data-testid="notifications-tab" className="w-full justify-start text-[13px] px-3 py-2 rounded-lg data-[state=active]:bg-[#192650] data-[state=active]:text-white">
                <i className="fas fa-bell mr-2.5 w-4 text-center text-xs"></i>
                Notifications
              </TabsTrigger>
              {user?.role === 'admin' && (
                <TabsTrigger value="catalog" data-testid="catalog-tab" className="w-full justify-start text-[13px] px-3 py-2 rounded-lg data-[state=active]:bg-[#192650] data-[state=active]:text-white">
                  <i className="fas fa-book mr-2.5 w-4 text-center text-xs"></i>
                  Catalog
                </TabsTrigger>
              )}
              {user?.role === 'admin' && (
                <TabsTrigger value="agreements" data-testid="agreements-tab" className="w-full justify-start text-[13px] px-3 py-2 rounded-lg data-[state=active]:bg-[#192650] data-[state=active]:text-white">
                  <i className="fas fa-file-signature mr-2.5 w-4 text-center text-xs"></i>
                  Agreements
                </TabsTrigger>
              )}
              {user?.role === 'admin' && (
                <TabsTrigger value="payment-schedules" data-testid="payment-schedules-tab" className="w-full justify-start text-[13px] px-3 py-2 rounded-lg data-[state=active]:bg-[#192650] data-[state=active]:text-white">
                  <i className="fas fa-rupee-sign mr-2.5 w-4 text-center text-xs"></i>
                  Payment Schedules
                </TabsTrigger>
              )}
              {user?.role === 'admin' && (
                <TabsTrigger value="backup" data-testid="backup-tab" className="w-full justify-start text-[13px] px-3 py-2 rounded-lg data-[state=active]:bg-[#192650] data-[state=active]:text-white">
                  <Database className="w-3.5 h-3.5 mr-2.5" />
                  Backup
                </TabsTrigger>
              )}
              {user?.role === 'admin' && (
                <TabsTrigger value="cleanup" data-testid="cleanup-tab" className="w-full justify-start text-[13px] px-3 py-2 rounded-lg data-[state=active]:bg-[#192650] data-[state=active]:text-white">
                  <i className="fas fa-broom mr-2.5 w-4 text-center text-xs"></i>
                  Cleanup
                </TabsTrigger>
              )}
            </TabsList>
          </div>

          {/* Mobile Navigation - Dropdown Select */}
          <div className="md:hidden px-1">
            <TabsList className="h-0 p-0 overflow-hidden">
              {/* Hidden triggers for Tabs to work — select below drives the value */}
              {['general','leads','intakes','incentives','pilot-workflow','finance','integrations','notifications','catalog','agreements','payment-schedules','backup','cleanup'].map(v => (
                <TabsTrigger key={v} value={v} className="hidden" />
              ))}
            </TabsList>
            <MobileSettingsNav user={user} activeTab={activeTab} onTabChange={setActiveTab} />
          </div>

          {/* Content Area */}
          <div className="flex-1 min-w-0 space-y-0">

        {/* Intakes Tab - Admin Only */}
        {user?.role === 'admin' && (
          <TabsContent value="intakes">
            <IntakeManagement />
          </TabsContent>
        )}

        {/* Pilot Workflow Tab - Admin / Operating Head */}
        {(user?.role === 'admin' || user?.role === 'operating_head') && (
          <TabsContent value="pilot-workflow">
            <WorkflowSettings />
          </TabsContent>
        )}

        {user?.role === 'admin' && <TabsContent value="leads"><WorkflowSettings /></TabsContent>}
        {user?.role === 'admin' && <TabsContent value="incentives"><IncentivePolicySettings /></TabsContent>}
        {/* Retired legacy controls: unreachable; historical values remain readable in the backend. */}
        {user?.role === 'admin' && (
          <TabsContent value="retired-lead-controls" hidden>
            <div className="space-y-4 sm:space-y-6">

              {/* Lead Assignment Mode — 3-mode hybrid pool system */}
              <Card>
                <CardContent className="p-4 sm:p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                      <i className="fas fa-random text-blue-600"></i>
                    </div>
                    <div>
                      <Label className="text-base font-semibold">Lead Assignment Mode</Label>
                      <p className="text-sm text-gray-500 mt-0.5">How new leads are routed to counsellors</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {[
                      { id: 'off', label: 'Pool Only', desc: 'Leads sit in pool until manually claimed', icon: 'fa-pause-circle', color: 'amber' },
                      { id: 'hybrid', label: 'Hybrid (Recommended)', desc: 'Pool with claim window, then auto-assign', icon: 'fa-bolt', color: 'indigo' },
                      { id: 'full_auto', label: 'Full Auto', desc: 'Auto-assign every new lead via round-robin', icon: 'fa-forward', color: 'emerald' },
                    ].map(opt => {
                      const active = (incentiveSettings.lead_assignment_mode || (incentiveSettings.auto_assign_leads === false ? 'off' : 'full_auto')) === opt.id;
                      return (
                        <button
                          key={opt.id}
                          type="button"
                          data-testid={`assignment-mode-${opt.id}`}
                          onClick={async () => {
                            const window_minutes = incentiveSettings.pool_claim_window_minutes || 15;
                            setIncentiveSettings({...incentiveSettings, lead_assignment_mode: opt.id, auto_assign_leads: opt.id === 'full_auto'});
                            try {
                              await apiClient.put('/leads/assignment-mode', { mode: opt.id, window_minutes });
                              toast.success(`Mode updated: ${opt.label}`);
                            } catch (e) {
                              toast.error('Failed to update assignment mode');
                            }
                          }}
                          className={`text-left rounded-lg border-2 p-3 transition ${active ? 'border-indigo-500 bg-indigo-50 shadow-sm' : 'border-gray-200 hover:border-indigo-300'}`}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <i className={`fas ${opt.icon} text-${opt.color}-600`}></i>
                            <span className="font-semibold text-sm">{opt.label}</span>
                            {active && <i className="fas fa-check-circle text-indigo-600 ml-auto"></i>}
                          </div>
                          <p className="text-xs text-gray-600 leading-tight">{opt.desc}</p>
                        </button>
                      );
                    })}
                  </div>
                  
                  {/* Window minutes input — only shown for hybrid mode */}
                  {(incentiveSettings.lead_assignment_mode === 'hybrid') && (
                    <div className="mt-4 flex items-center gap-3 p-3 rounded-lg bg-indigo-50 border border-indigo-200">
                      <i className="fas fa-stopwatch text-indigo-600"></i>
                      <Label className="text-sm font-medium whitespace-nowrap">Claim window:</Label>
                      <Input
                        type="number"
                        min="1"
                        max="240"
                        data-testid="pool-window-input"
                        value={incentiveSettings.pool_claim_window_minutes || 15}
                        onChange={(e) => setIncentiveSettings({...incentiveSettings, pool_claim_window_minutes: parseInt(e.target.value) || 15})}
                        className="w-24"
                      />
                      <span className="text-sm text-gray-600">minutes — counsellors can claim within this time, then auto-assigned</span>
                      <Button
                        size="sm"
                        data-testid="save-pool-window-btn"
                        onClick={async () => {
                          try {
                            await apiClient.put('/leads/assignment-mode', {
                              mode: 'hybrid',
                              window_minutes: incentiveSettings.pool_claim_window_minutes || 15,
                            });
                            toast.success(`Claim window saved: ${incentiveSettings.pool_claim_window_minutes || 15} min`);
                          } catch (e) {
                            toast.error('Failed to save window');
                          }
                        }}
                      >Save</Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* RNR Threshold Setting */}
              <Card>
                <CardHeader>
                  <CardTitle>
                    <i className="fas fa-phone-slash mr-2 text-orange-500"></i>
                    RNR (Not Connected) Policy
                  </CardTitle>
                  <CardDescription>
                    Configure how many consecutive not-connected calls move a lead to Bad Leads
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <Label htmlFor="rnr_threshold">Consecutive RNR calls before Bad Lead</Label>
                      <p className="text-xs text-gray-500 mt-1">
                        When a lead has this many consecutive not-connected calls, they are automatically moved to Bad Leads
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Input
                        id="rnr_threshold"
                        data-testid="rnr-threshold-input"
                        type="number"
                        min={1}
                        max={20}
                        value={rnrThreshold}
                        onChange={(e) => setRnrThreshold(parseInt(e.target.value) || 5)}
                        className="w-20 text-center text-lg font-bold"
                      />
                      <span className="text-sm text-gray-500">calls</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Button
                      data-testid="save-rnr-threshold-btn"
                      onClick={handleSaveRnrThreshold}
                      disabled={savingRnrThreshold}
                      className="bg-orange-600 hover:bg-orange-700"
                    >
                      {savingRnrThreshold ? (
                        <><i className="fas fa-spinner fa-spin mr-2"></i>Saving...</>
                      ) : (
                        <><i className="fas fa-save mr-2"></i>Save RNR Policy</>
                      )}
                    </Button>
                    <span className="text-sm text-gray-500">
                      Current: <strong>{rnrThreshold}</strong> consecutive not-connected calls = Bad Lead
                    </span>
                  </div>
                </CardContent>
              </Card>

              {/* Intake Periods moved to dedicated "Intakes" side-menu page */}
            </div>
          </TabsContent>
        )}

        {/* Incentives Tab - Admin Only */}
        {user?.role === 'admin' && (
          <TabsContent value="retired-incentive-controls" hidden>
            <div className="space-y-4 sm:space-y-6">
              {loadingIncentives ? (
                <Card>
                  <CardContent className="p-8 text-center">
                    <i className="fas fa-spinner fa-spin text-2xl text-gray-400 mb-2"></i>
                    <p className="text-gray-500">Loading incentive settings...</p>
                  </CardContent>
                </Card>
              ) : (
                <>
                  {/* System Toggle */}
                  <Card>
                    <CardHeader className="p-3 sm:p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                            <i className="fas fa-power-off text-purple-600 text-xl"></i>
                          </div>
                          <div>
                            <CardTitle className="text-lg">Incentive System</CardTitle>
                            <CardDescription>Enable or disable the entire incentive calculation system</CardDescription>
                          </div>
                        </div>
                        <Switch
                          checked={incentiveSettings.is_enabled}
                          onCheckedChange={(checked) => setIncentiveSettings({...incentiveSettings, is_enabled: checked})}
                          data-testid="incentive-system-toggle"
                        />
                      </div>
                    </CardHeader>
                  </Card>

                  {/* Base Commission Settings */}
                  <Card>
                    <CardHeader className="p-3 sm:p-6">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                          <i className="fas fa-rupee-sign text-green-600 text-xl"></i>
                        </div>
                        <div>
                          <CardTitle className="text-lg">Base Commission</CardTitle>
                          <CardDescription>Commission per successful admission (Flown + Paid)</CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="p-3 sm:p-6 pt-0">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="mbbs_commission" className="text-sm font-medium">MBBS Program (₹)</Label>
                          <Input
                            id="mbbs_commission"
                            type="number"
                            value={incentiveSettings.mbbs_commission}
                            onChange={(e) => setIncentiveSettings({...incentiveSettings, mbbs_commission: parseFloat(e.target.value)})}
                            className="mt-1"
                            data-testid="mbbs-commission-input"
                          />
                          <p className="text-xs text-gray-500 mt-1">Default: ₹10,000</p>
                        </div>
                        <div>
                          <Label htmlFor="other_commission" className="text-sm font-medium">Other Programs (₹)</Label>
                          <Input
                            id="other_commission"
                            type="number"
                            value={incentiveSettings.other_commission}
                            onChange={(e) => setIncentiveSettings({...incentiveSettings, other_commission: parseFloat(e.target.value)})}
                            className="mt-1"
                            data-testid="other-commission-input"
                          />
                          <p className="text-xs text-gray-500 mt-1">Default: ₹5,000</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Power Hours Settings */}
                  <Card>
                    <CardHeader className="p-3 sm:p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                            <i className="fas fa-bolt text-yellow-600 text-xl"></i>
                          </div>
                          <div>
                            <CardTitle className="text-lg">Power Hours</CardTitle>
                            <CardDescription>Day-specific lead claiming windows with bonus commission</CardDescription>
                          </div>
                        </div>
                        <Switch
                          checked={incentiveSettings.power_hours_enabled}
                          onCheckedChange={(checked) => setIncentiveSettings({...incentiveSettings, power_hours_enabled: checked})}
                          data-testid="power-hours-toggle"
                        />
                      </div>
                    </CardHeader>
                    <CardContent className="p-3 sm:p-6 pt-0 space-y-4">
                      {/* Sunday Schedule (Full Day) */}
                      <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                        <div className="flex items-center gap-2 mb-3">
                          <i className="fas fa-sun text-orange-500"></i>
                          <span className="font-medium text-gray-800">Sunday Schedule</span>
                          <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded">Full Day</span>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-sm text-gray-600">Start Time (IST)</Label>
                            <Input
                              type="time"
                              value={incentiveSettings.power_hours_sunday_start || '10:00'}
                              onChange={(e) => setIncentiveSettings({...incentiveSettings, power_hours_sunday_start: e.target.value})}
                              className="mt-1"
                              data-testid="power-hours-sunday-start"
                            />
                          </div>
                          <div>
                            <Label className="text-sm text-gray-600">End Time (IST)</Label>
                            <Input
                              type="time"
                              value={incentiveSettings.power_hours_sunday_end || '22:00'}
                              onChange={(e) => setIncentiveSettings({...incentiveSettings, power_hours_sunday_end: e.target.value})}
                              className="mt-1"
                              data-testid="power-hours-sunday-end"
                            />
                          </div>
                        </div>
                        <p className="text-xs text-orange-700 mt-2">Default: 10:00 AM - 10:00 PM</p>
                      </div>

                      {/* Weekday Schedule (Evening) */}
                      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="flex items-center gap-2 mb-3">
                          <i className="fas fa-briefcase text-blue-500"></i>
                          <span className="font-medium text-gray-800">Weekday Schedule (Mon-Sat)</span>
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Evening</span>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-sm text-gray-600">Start Time (IST)</Label>
                            <Input
                              type="time"
                              value={incentiveSettings.power_hours_weekday_start || '19:00'}
                              onChange={(e) => setIncentiveSettings({
                                ...incentiveSettings, 
                                power_hours_weekday_start: e.target.value,
                                power_hours_start: e.target.value // Keep legacy field in sync
                              })}
                              className="mt-1"
                              data-testid="power-hours-weekday-start"
                            />
                          </div>
                          <div>
                            <Label className="text-sm text-gray-600">End Time (IST)</Label>
                            <Input
                              type="time"
                              value={incentiveSettings.power_hours_weekday_end || '22:00'}
                              onChange={(e) => setIncentiveSettings({
                                ...incentiveSettings, 
                                power_hours_weekday_end: e.target.value,
                                power_hours_end: e.target.value // Keep legacy field in sync
                              })}
                              className="mt-1"
                              data-testid="power-hours-weekday-end"
                            />
                          </div>
                        </div>
                        <p className="text-xs text-blue-700 mt-2">Default: 7:00 PM - 10:00 PM</p>
                      </div>

                      {/* Bonus Percentage */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="power_hours_bonus" className="text-sm font-medium">Bonus Percentage (%)</Label>
                          <Input
                            id="power_hours_bonus"
                            type="number"
                            value={incentiveSettings.power_hours_bonus_percentage}
                            onChange={(e) => setIncentiveSettings({...incentiveSettings, power_hours_bonus_percentage: parseFloat(e.target.value)})}
                            className="mt-1"
                            data-testid="power-hours-bonus"
                          />
                          <p className="text-xs text-gray-500 mt-1">Extra % added to base commission</p>
                        </div>
                      </div>

                      <div className="mt-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                        <p className="text-sm text-yellow-800">
                          <i className="fas fa-info-circle mr-2"></i>
                          <strong>How it works:</strong> During Power Hours, new leads are NOT auto-assigned. 
                          All counselors can see and claim available leads in the "New" tab. 
                          First to claim gets the lead. If they make a <strong>connected call during Power Hours</strong>, 
                          they earn the <strong>+{incentiveSettings.power_hours_bonus_percentage}% bonus</strong> when the admission is confirmed.
                        </p>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Weekly Performance Bonus */}
                  <Card>
                    <CardHeader className="p-3 sm:p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                            <i className="fas fa-calendar-week text-blue-600 text-xl"></i>
                          </div>
                          <div>
                            <CardTitle className="text-lg">Weekly Performance Bonus</CardTitle>
                            <CardDescription>Bonus for achieving minimum admissions per week (Mon-Sun)</CardDescription>
                          </div>
                        </div>
                        <Switch
                          checked={incentiveSettings.weekly_bonus_enabled}
                          onCheckedChange={(checked) => setIncentiveSettings({...incentiveSettings, weekly_bonus_enabled: checked})}
                          data-testid="weekly-bonus-toggle"
                        />
                      </div>
                    </CardHeader>
                    <CardContent className="p-3 sm:p-6 pt-0">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="weekly_min" className="text-sm font-medium">Minimum Admissions</Label>
                          <Input
                            id="weekly_min"
                            type="number"
                            value={incentiveSettings.weekly_min_admissions}
                            onChange={(e) => setIncentiveSettings({...incentiveSettings, weekly_min_admissions: parseInt(e.target.value)})}
                            className="mt-1"
                            data-testid="weekly-min-admissions"
                          />
                        </div>
                        <div>
                          <Label htmlFor="weekly_bonus" className="text-sm font-medium">Bonus Amount (₹)</Label>
                          <Input
                            id="weekly_bonus"
                            type="number"
                            value={incentiveSettings.weekly_bonus_amount}
                            onChange={(e) => setIncentiveSettings({...incentiveSettings, weekly_bonus_amount: parseFloat(e.target.value)})}
                            className="mt-1"
                            data-testid="weekly-bonus-amount"
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Monthly Champion */}
                  <Card>
                    <CardHeader className="p-3 sm:p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                            <i className="fas fa-crown text-orange-600 text-xl"></i>
                          </div>
                          <div>
                            <CardTitle className="text-lg">Monthly Admission Champion</CardTitle>
                            <CardDescription>Bonus for the counselor with highest admissions in the month</CardDescription>
                          </div>
                        </div>
                        <Switch
                          checked={incentiveSettings.monthly_champion_enabled}
                          onCheckedChange={(checked) => setIncentiveSettings({...incentiveSettings, monthly_champion_enabled: checked})}
                          data-testid="monthly-champion-toggle"
                        />
                      </div>
                    </CardHeader>
                    <CardContent className="p-3 sm:p-6 pt-0">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="monthly_min" className="text-sm font-medium">Minimum Admissions to Qualify</Label>
                          <Input
                            id="monthly_min"
                            type="number"
                            value={incentiveSettings.monthly_champion_min_admissions}
                            onChange={(e) => setIncentiveSettings({...incentiveSettings, monthly_champion_min_admissions: parseInt(e.target.value)})}
                            className="mt-1"
                            data-testid="monthly-min-admissions"
                          />
                        </div>
                        <div>
                          <Label htmlFor="monthly_bonus" className="text-sm font-medium">Champion Bonus (₹)</Label>
                          <Input
                            id="monthly_bonus"
                            type="number"
                            value={incentiveSettings.monthly_champion_bonus}
                            onChange={(e) => setIncentiveSettings({...incentiveSettings, monthly_champion_bonus: parseFloat(e.target.value)})}
                            className="mt-1"
                            data-testid="monthly-champion-bonus"
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* First Strike Champion */}
                  <Card>
                    <CardHeader className="p-3 sm:p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                            <i className="fas fa-stopwatch text-red-600 text-xl"></i>
                          </div>
                          <div>
                            <CardTitle className="text-lg">First Strike Champion</CardTitle>
                            <CardDescription>Bonus for most connected calls within {incentiveSettings.first_strike_window_minutes} minutes of lead creation</CardDescription>
                          </div>
                        </div>
                        <Switch
                          checked={incentiveSettings.first_strike_enabled}
                          onCheckedChange={(checked) => setIncentiveSettings({...incentiveSettings, first_strike_enabled: checked})}
                          data-testid="first-strike-toggle"
                        />
                      </div>
                    </CardHeader>
                    <CardContent className="p-3 sm:p-6 pt-0">
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                          <Label htmlFor="first_strike_window" className="text-sm font-medium">Time Window (minutes)</Label>
                          <Input
                            id="first_strike_window"
                            type="number"
                            value={incentiveSettings.first_strike_window_minutes}
                            onChange={(e) => setIncentiveSettings({...incentiveSettings, first_strike_window_minutes: parseInt(e.target.value)})}
                            className="mt-1"
                            data-testid="first-strike-window"
                          />
                        </div>
                        <div>
                          <Label htmlFor="first_strike_min_calls" className="text-sm font-medium">Min Calls to Qualify</Label>
                          <Input
                            id="first_strike_min_calls"
                            type="number"
                            value={incentiveSettings.first_strike_min_calls ?? 30}
                            onChange={(e) => setIncentiveSettings({...incentiveSettings, first_strike_min_calls: parseInt(e.target.value) || 0})}
                            className="mt-1"
                            data-testid="first-strike-min-calls"
                          />
                          <p className="text-[10px] text-gray-500 mt-1">Counsellor must have ≥ this many first-strike calls</p>
                        </div>
                        <div>
                          <Label htmlFor="first_strike_bonus" className="text-sm font-medium">Champion Bonus (₹)</Label>
                          <Input
                            id="first_strike_bonus"
                            type="number"
                            value={incentiveSettings.first_strike_bonus}
                            onChange={(e) => setIncentiveSettings({...incentiveSettings, first_strike_bonus: parseFloat(e.target.value)})}
                            className="mt-1"
                            data-testid="first-strike-bonus"
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Team Performance Bonus */}
                  <Card>
                    <CardHeader className="p-3 sm:p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                            <i className="fas fa-users text-indigo-600 text-xl"></i>
                          </div>
                          <div>
                            <CardTitle className="text-lg">Team Performance Bonus</CardTitle>
                            <CardDescription>Bonus when team achieves (Members × Target) + Extra admissions</CardDescription>
                          </div>
                        </div>
                        <Switch
                          checked={incentiveSettings.team_bonus_enabled}
                          onCheckedChange={(checked) => setIncentiveSettings({...incentiveSettings, team_bonus_enabled: checked})}
                          data-testid="team-bonus-toggle"
                        />
                      </div>
                    </CardHeader>
                    <CardContent className="p-3 sm:p-6 pt-0">
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div>
                          <Label htmlFor="team_multiplier" className="text-sm font-medium">Target per Member</Label>
                          <Input
                            id="team_multiplier"
                            type="number"
                            value={incentiveSettings.team_target_multiplier}
                            onChange={(e) => setIncentiveSettings({...incentiveSettings, team_target_multiplier: parseInt(e.target.value)})}
                            className="mt-1"
                            data-testid="team-target-multiplier"
                          />
                          <p className="text-xs text-gray-500 mt-1">Team Target = Members × {incentiveSettings.team_target_multiplier || 5}</p>
                        </div>
                        <div>
                          <Label htmlFor="team_extra" className="text-sm font-medium">Extra to Qualify</Label>
                          <Input
                            id="team_extra"
                            type="number"
                            value={incentiveSettings.team_bonus_extra_threshold}
                            onChange={(e) => setIncentiveSettings({...incentiveSettings, team_bonus_extra_threshold: parseInt(e.target.value)})}
                            className="mt-1"
                            data-testid="team-extra-threshold"
                          />
                          <p className="text-xs text-gray-500 mt-1">Bonus at Target + {incentiveSettings.team_bonus_extra_threshold || 5}</p>
                        </div>
                        <div>
                          <Label htmlFor="team_member_min" className="text-sm font-medium">Member Min Admissions</Label>
                          <Input
                            id="team_member_min"
                            type="number"
                            value={incentiveSettings.team_member_min_admissions}
                            onChange={(e) => setIncentiveSettings({...incentiveSettings, team_member_min_admissions: parseInt(e.target.value)})}
                            className="mt-1"
                            data-testid="team-member-min"
                          />
                          <p className="text-xs text-gray-500 mt-1">Participation requirement</p>
                        </div>
                        <div>
                          <Label htmlFor="team_bonus" className="text-sm font-medium">Bonus per Member (₹)</Label>
                          <Input
                            id="team_bonus"
                            type="number"
                            value={incentiveSettings.team_bonus_amount}
                            onChange={(e) => setIncentiveSettings({...incentiveSettings, team_bonus_amount: parseFloat(e.target.value)})}
                            className="mt-1"
                            data-testid="team-bonus-amount"
                          />
                        </div>
                      </div>
                      <div className="mt-4 p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                        <p className="text-sm text-indigo-800 font-medium mb-1">
                          <i className="fas fa-calculator mr-2"></i>
                          Formula Example:
                        </p>
                        <p className="text-xs text-indigo-700">
                          • Team of 3: Target = 3 × {incentiveSettings.team_target_multiplier || 5} = {3 * (incentiveSettings.team_target_multiplier || 5)}, 
                          Bonus at {3 * (incentiveSettings.team_target_multiplier || 5) + (incentiveSettings.team_bonus_extra_threshold || 5)} admissions
                          (min 2 members with {incentiveSettings.team_member_min_admissions || 5}+ each)
                        </p>
                        <p className="text-xs text-indigo-700">
                          • Team of 5: Target = 5 × {incentiveSettings.team_target_multiplier || 5} = {5 * (incentiveSettings.team_target_multiplier || 5)}, 
                          Bonus at {5 * (incentiveSettings.team_target_multiplier || 5) + (incentiveSettings.team_bonus_extra_threshold || 5)} admissions
                          (min 3 members with {incentiveSettings.team_member_min_admissions || 5}+ each)
                        </p>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Intake Champion Bonus */}
                  <Card>
                    <CardHeader className="p-3 sm:p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                            <i className="fas fa-award text-emerald-600 text-xl"></i>
                          </div>
                          <div>
                            <CardTitle className="text-lg">Intake Champion Bonus</CardTitle>
                            <CardDescription>Grand prize for highest performer during intake period</CardDescription>
                          </div>
                        </div>
                        <Switch
                          checked={incentiveSettings.intake_champion_enabled}
                          onCheckedChange={(checked) => setIncentiveSettings({...incentiveSettings, intake_champion_enabled: checked})}
                          data-testid="intake-champion-toggle"
                        />
                      </div>
                    </CardHeader>
                    <CardContent className="p-3 sm:p-6 pt-0">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="intake_min" className="text-sm font-medium">Minimum Admissions to Qualify</Label>
                          <Input
                            id="intake_min"
                            type="number"
                            value={incentiveSettings.intake_champion_min_admissions}
                            onChange={(e) => setIncentiveSettings({...incentiveSettings, intake_champion_min_admissions: parseInt(e.target.value)})}
                            className="mt-1"
                            data-testid="intake-min-admissions"
                          />
                        </div>
                        <div>
                          <Label htmlFor="intake_bonus" className="text-sm font-medium">Champion Bonus (₹)</Label>
                          <Input
                            id="intake_bonus"
                            type="number"
                            value={incentiveSettings.intake_champion_bonus}
                            onChange={(e) => setIncentiveSettings({...incentiveSettings, intake_champion_bonus: parseFloat(e.target.value)})}
                            className="mt-1"
                            data-testid="intake-champion-bonus"
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Save Button */}
                  <div className="flex justify-end">
                    <Button
                      onClick={handleSaveIncentiveSettings}
                      disabled={savingIncentives}
                      className="bg-green-600 hover:bg-green-700"
                      data-testid="save-incentive-settings"
                    >
                      {savingIncentives ? (
                        <>
                          <i className="fas fa-spinner fa-spin mr-2"></i>
                          Saving...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save mr-2"></i>
                          Save Incentive Settings
                        </>
                      )}
                    </Button>
                  </div>
                </>
              )}
            </div>
          </TabsContent>
        )}

        {/* Finance Settings Tab - Admin & Accountant */}
        {(user?.role === 'admin' || user?.role === 'accountant') && (
          <TabsContent value="finance">
            <FinanceSettings embedded />
          </TabsContent>
        )}

        {/* Catalog Tab - Admin Only */}
        {user?.role === 'admin' && (
          <TabsContent value="catalog">
            <CatalogManagement />
          </TabsContent>
        )}

        {/* Agreement Templates Tab - Admin Only */}
        {user?.role === 'admin' && (
          <TabsContent value="agreements">
            <AgreementTemplatesManager />
          </TabsContent>
        )}

        {/* Payment Schedules Tab - Admin Only */}
        {user?.role === 'admin' && (
          <TabsContent value="payment-schedules">
            <PaymentSchedulesManager user={user} />
          </TabsContent>
        )}

        {/* Backup Tab - Admin Only */}
        {user?.role === 'admin' && (
          <TabsContent value="backup">
            <div className="space-y-6">
              {/* Info Cards */}
              <div className="grid grid-cols-3 gap-2 sm:gap-4">
                <Card>
                  <CardHeader className="pb-1 sm:pb-2 p-2 sm:p-4">
                    <CardTitle className="text-[10px] sm:text-sm font-medium flex items-center gap-1 sm:gap-2">
                      <Shield className="w-3 h-3 sm:w-4 sm:h-4 text-green-600" />
                      <span className="hidden sm:inline">Auto Backup</span>
                      <span className="sm:hidden">Auto</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-2 sm:p-4 pt-0">
                    <div className="text-lg sm:text-2xl font-bold text-green-600">Daily</div>
                    <p className="text-[9px] sm:text-xs text-gray-600 mt-0.5 sm:mt-1">At midnight</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-1 sm:pb-2 p-2 sm:p-4">
                    <CardTitle className="text-[10px] sm:text-sm font-medium flex items-center gap-1 sm:gap-2">
                      <Calendar className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600" />
                      <span className="hidden sm:inline">Retention</span>
                      <span className="sm:hidden">Keep</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-2 sm:p-4 pt-0">
                    <div className="text-lg sm:text-2xl font-bold text-blue-600">30d</div>
                    <p className="text-[9px] sm:text-xs text-gray-600 mt-0.5 sm:mt-1">Auto-delete</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-1 sm:pb-2 p-2 sm:p-4">
                    <CardTitle className="text-[10px] sm:text-sm font-medium flex items-center gap-1 sm:gap-2">
                      <HardDrive className="w-3 h-3 sm:w-4 sm:h-4 text-purple-600" />
                      <span className="hidden sm:inline">Total Backups</span>
                      <span className="sm:hidden">Total</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-2 sm:p-4 pt-0">
                    <div className="text-lg sm:text-2xl font-bold text-purple-600">{backups.length}</div>
                    <p className="text-[9px] sm:text-xs text-gray-600 mt-0.5 sm:mt-1">Available</p>
                  </CardContent>
                </Card>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2 sm:gap-3">
                <Button
                  onClick={createBackup}
                  disabled={creatingBackup}
                  className="bg-green-600 hover:bg-green-700 text-xs sm:text-sm"
                  size="sm"
                  data-testid="create-backup-btn"
                >
                  {creatingBackup ? (
                    <>
                      <RefreshCw className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 animate-spin" />
                      <span className="hidden sm:inline">Creating...</span>
                      <span className="sm:hidden">...</span>
                    </>
                  ) : (
                    <>
                      <Download className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                      <span className="hidden sm:inline">Create Manual Backup</span>
                      <span className="sm:hidden">Backup</span>
                    </>
                  )}
                </Button>

                <Button
                  onClick={fetchBackups}
                  disabled={loadingBackups}
                  variant="outline"
                  size="sm"
                  className="text-xs sm:text-sm"
                  data-testid="refresh-backups-btn"
                >
                  <RefreshCw className={`w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 ${loadingBackups ? 'animate-spin' : ''}`} />
                  <span className="hidden sm:inline">Refresh List</span>
                  <span className="sm:hidden">Refresh</span>
                </Button>

                {/* Import Backup from File */}
                <div className="relative">
                  <input
                    type="file"
                    accept=".json"
                    onChange={handleImportBackup}
                    disabled={importingBackup}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
                    data-testid="import-backup-input"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs sm:text-sm border-amber-500 text-amber-600 hover:bg-amber-50"
                    disabled={importingBackup}
                    data-testid="import-backup-btn"
                  >
                    {importingBackup ? (
                      <>
                        <RefreshCw className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 animate-spin" />
                        <span className="hidden sm:inline">Importing...</span>
                        <span className="sm:hidden">...</span>
                      </>
                    ) : (
                      <>
                        <i className="fas fa-file-import mr-1 sm:mr-2 text-xs"></i>
                        <span className="hidden sm:inline">Import from File</span>
                        <span className="sm:hidden">Import</span>
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Backups List */}
              <Card>
                <CardHeader>
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Database className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <CardTitle>Available Backups</CardTitle>
                      <CardDescription>View and restore previous backups of your database</CardDescription>
                    </div>
                    {backupInProgress && (
                      <div
                        className="ml-auto inline-flex items-center gap-2 rounded-full bg-blue-50 border border-blue-200 px-3 py-1.5 text-xs sm:text-sm font-medium text-blue-700"
                        data-testid="backup-in-progress-chip"
                      >
                        <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                        Backup in progress…
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {loadingBackups && backups.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <RefreshCw className="w-8 h-8 mx-auto mb-2 animate-spin" />
                      Loading backups...
                    </div>
                  ) : backups.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Database className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>No backups available yet</p>
                      <p className="text-sm mt-1">Create your first backup manually or wait for automatic backup</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {backups.map((backup) => (
                        <div
                          key={backup.backup_name}
                          className={`flex flex-col sm:flex-row sm:items-center justify-between p-3 sm:p-4 border rounded-lg gap-3 transition-colors ${backup.backup_name === newBackupName ? 'bg-green-50 border-green-300 ring-1 ring-green-200' : 'hover:bg-gray-50'}`}
                          data-testid={`backup-row-${backup.backup_name}`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-1 sm:gap-2 mb-1">
                              <h3 className="font-semibold text-gray-900 text-xs sm:text-sm truncate max-w-[200px] sm:max-w-none">
                                {backup.backup_name}
                              </h3>
                              <Badge variant={backup.backup_type === 'auto' ? 'default' : 'secondary'} className="text-[9px] sm:text-xs">
                                {backup.backup_type}
                              </Badge>
                              <Badge variant={backup.status === 'completed' ? 'outline' : 'destructive'} className="text-[9px] sm:text-xs">
                                {backup.status}
                              </Badge>
                            </div>
                            <div className="text-[10px] sm:text-sm text-gray-600 space-y-0.5">
                              <p><span className="font-medium">Date:</span> {formatBackupDate(backup.timestamp)}</p>
                              {backup.total_documents && (
                                <p><span className="font-medium">Docs:</span> {backup.total_documents.toLocaleString()}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-2 self-end sm:self-center">
                            <Button
                              onClick={() => downloadBackup(backup.backup_name)}
                              variant="outline"
                              size="sm"
                              className="text-xs h-8 px-2 sm:px-3"
                              data-testid={`download-backup-${backup.backup_name}`}
                            >
                              <Download className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
                              <span className="hidden sm:inline">Download</span>
                            </Button>
                            <Button
                              onClick={() => restoreBackup(backup.backup_name)}
                              variant="destructive"
                              size="sm"
                              disabled={loadingBackups}
                              className="text-xs h-8 px-2 sm:px-3"
                              data-testid={`restore-backup-${backup.backup_name}`}
                            >
                              <RefreshCw className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
                              <span className="hidden sm:inline">Restore</span>
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Warning Notice */}
              <Card className="border-amber-200 bg-amber-50">
                <CardHeader>
                  <CardTitle className="text-amber-900 flex items-center gap-2 text-base">
                    <Shield className="w-5 h-5" />
                    Important Notes
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-amber-800 space-y-1">
                  <p>• Automatic backups run daily at midnight (00:00)</p>
                  <p>• Backups are stored for 30 days, then automatically deleted</p>
                  <p>• Restoring a backup will REPLACE ALL current data - use with extreme caution!</p>
                  <p>• Always create a manual backup before making major changes</p>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        )}

        {user?.role === 'admin' && (
          <TabsContent value="cleanup">
            <p data-testid="overdue-cleanup-retired">Overdue records are retained. Archival cleanup has been retired.</p>
          </TabsContent>
        )}

        <TabsContent value="integrations">
          <div className="space-y-3">
            {/* WATI WhatsApp Integration - Patrick's Account */}
            <IntegrationCollapsible
              id="wati-patrick"
              name="WATI WhatsApp — Patrick's Account"
              subtitle="Patrick's Edu Care WhatsApp lead capture"
              iconClass="fab fa-whatsapp text-green-600"
              iconBg="bg-green-100"
              badge={<IntegrationHealthBadge account="patrick" compact />}
              open={openIntegration === 'wati-patrick'}
              onToggle={toggleIntegration}
            >
                <IntegrationHealthBadge account="patrick" />
                {/* Webhook URL - Copy this to WATI Dashboard */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <i className="fas fa-info-circle text-blue-600 mt-1"></i>
                    <div className="flex-1">
                      <h4 className="font-medium text-blue-900 mb-2">Webhook URL for Patrick's WATI</h4>
                      <p className="text-sm text-blue-800 mb-3">
                        Copy this URL and configure it in Patrick's WATI Dashboard → Settings → Integrations → Webhooks
                      </p>
                      <div className="flex items-center space-x-2">
                        <Input
                          value={`${window.location.origin}/api/webhook/wati-patrick`}
                          readOnly
                          className="font-mono text-sm bg-white"
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            navigator.clipboard.writeText(`${window.location.origin}/api/webhook/wati-patrick`);
                            toast.success('Patrick webhook URL copied!');
                          }}
                        >
                          <i className="fas fa-copy mr-2"></i>
                          Copy
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="text-sm text-gray-600">
                  <p><strong>Endpoint:</strong> {watiConfig.patrick_endpoint || 'https://live-mt-server.wati.io/435749'}</p>
                  <p><strong>Lead Source:</strong> wati-patrick</p>
                  <p><strong>Round-Robin:</strong> Separate rotation for Patrick's leads</p>
                </div>

                {/* Nursing Flow Webhook URL */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <i className="fas fa-clipboard-list text-amber-600 mt-1"></i>
                    <div className="flex-1">
                      <h4 className="font-medium text-amber-900 mb-2">Nursing Flow Webhook URL</h4>
                      <p className="text-sm text-amber-800 mb-3">
                        Use this URL for the Nursing Enquiry WhatsApp Flow in WATI
                      </p>
                      <div className="flex items-center space-x-2">
                        <Input
                          value={`${window.location.origin}/api/webhook/wati-nursing-flow`}
                          readOnly
                          className="font-mono text-sm bg-white"
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            navigator.clipboard.writeText(`${window.location.origin}/api/webhook/wati-nursing-flow`);
                            toast.success('Nursing Flow webhook URL copied!');
                          }}
                        >
                          <i className="fas fa-copy mr-2"></i>
                          Copy
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* WATI API Configuration */}
                <div className="border-t pt-4 mt-4">
                  <h4 className="font-medium text-gray-900 text-sm mb-3">
                    <i className="fas fa-key mr-2 text-gray-500"></i>
                    API Configuration
                  </h4>
                  <div className="space-y-3">
                    <div>
                      <label className="text-xs text-gray-600 block mb-1">API Endpoint</label>
                      <Input
                        data-testid="wati-patrick-endpoint"
                        placeholder="https://live-mt-server.wati.io/435749"
                        value={watiConfig.patrick_endpoint}
                        onChange={(e) => setWatiConfig(prev => ({ ...prev, patrick_endpoint: e.target.value }))}
                        className="font-mono text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-600 block mb-1">
                        API Token 
                        {watiConfig.patrick_token_set && (
                          <span className="ml-2 text-green-600 font-normal">
                            (Current: {watiConfig.patrick_token_masked})
                          </span>
                        )}
                      </label>
                      <Input
                        data-testid="wati-patrick-token"
                        type="password"
                        placeholder={watiConfig.patrick_token_set ? "Enter new token to update..." : "Paste WATI API token (without 'Bearer' prefix)"}
                        value={watiConfig.patrick_token}
                        onChange={(e) => setWatiConfig(prev => ({ ...prev, patrick_token: e.target.value }))}
                        className="font-mono text-sm"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        onClick={() => handleSaveWatiConfig('patrick')}
                        disabled={savingWati || (!watiConfig.patrick_endpoint && !watiConfig.patrick_token)}
                        className="text-xs h-8"
                        data-testid="save-wati-patrick-btn"
                      >
                        {savingWati ? <><i className="fas fa-spinner fa-spin mr-1"></i>Saving...</> : <><i className="fas fa-save mr-1"></i>Save API Config</>}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleTestWatiConnection('patrick')}
                        disabled={watiTestingPatrick}
                        className="text-xs h-8"
                        data-testid="test-wati-patrick-btn"
                      >
                        {watiTestingPatrick ? <><i className="fas fa-spinner fa-spin mr-1"></i>Testing...</> : <><i className="fas fa-plug mr-1"></i>Test Connection</>}
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Counselor Selection for Patrick */}
                <div className="border-t pt-4 mt-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                    <h4 className="font-medium text-gray-900 text-sm">Assign Counselors</h4>
                    <div className="flex space-x-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => selectAllCounselors('patrick')}
                        disabled={loadingCounselors}
                        className="text-xs px-2 h-7"
                      >
                        <i className="fas fa-check-double mr-1"></i>
                        All
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => clearAllCounselors('patrick')}
                        disabled={loadingCounselors}
                        className="text-xs px-2 h-7"
                      >
                        <i className="fas fa-times mr-1"></i>
                        Clear
                      </Button>
                    </div>
                  </div>
                  
                  {loadingCounselors ? (
                    <div className="text-center py-4 text-gray-500">
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      Loading counselors...
                    </div>
                  ) : counselors.length === 0 ? (
                    <div className="text-center py-4 text-gray-500">
                      No active counselors found
                    </div>
                  ) : (
                    <div className="space-y-2 mb-4 max-h-48 overflow-y-auto">
                      {counselors.map((counselor) => (
                        <label 
                          key={counselor.id} 
                          htmlFor={`patrick-${counselor.id}`}
                          className="flex items-center gap-2 cursor-pointer p-1.5 hover:bg-gray-50 rounded"
                        >
                          <input
                            type="checkbox"
                            id={`patrick-${counselor.id}`}
                            checked={patrickCounselors.includes(counselor.id)}
                            onChange={() => toggleCounselor(counselor.id, 'patrick')}
                            className="w-4 h-4 flex-shrink-0 text-green-600 rounded focus:ring-green-500"
                          />
                          <span className="text-xs sm:text-sm text-gray-700 truncate">
                            {counselor.full_name}
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                  
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <p className="text-xs text-gray-600">
                      {patrickCounselors.length === 0 
                        ? "⚠️ No counselors - will assign to ALL" 
                        : `✅ ${patrickCounselors.length} selected`}
                    </p>
                    <Button
                      onClick={() => handleSaveCounselorAssignment('patrick')}
                      disabled={saving || loadingCounselors}
                      size="sm"
                      className="text-xs h-8"
                    >
                      {saving ? (
                        <>
                          <i className="fas fa-spinner fa-spin mr-1"></i>
                          Saving...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save mr-1"></i>
                          Save
                        </>
                      )}
                    </Button>
                  </div>
                </div>
            </IntegrationCollapsible>

            {/* AiSensy WhatsApp Integration - Gradbud Account (migrated from WATI, June 2026) */}
            <IntegrationCollapsible
              id="wati-gradbud"
              name="AiSensy WhatsApp — Gradbud Account"
              subtitle="Gradbud WhatsApp lead capture (AiSensy)"
              iconClass="fab fa-whatsapp text-green-600"
              iconBg="bg-green-100"
              open={openIntegration === 'wati-gradbud'}
              onToggle={toggleIntegration}
            >
                {/* Webhook URL - Copy this to AiSensy Dashboard */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <i className="fas fa-info-circle text-green-600 mt-1"></i>
                    <div className="flex-1">
                      <h4 className="font-medium text-green-900 mb-2">Webhook URL for Gradbud AiSensy</h4>
                      <p className="text-sm text-green-800 mb-3">
                        Copy this URL and configure it in the Gradbud AiSensy Dashboard → Developer / Integrations → Webhooks (Incoming Messages).
                      </p>
                      <div className="flex items-center space-x-2">
                        <Input
                          data-testid="aisensy-gradbud-webhook-url"
                          value={`${window.location.origin}/api/webhook/aisensy-gradbud`}
                          readOnly
                          className="font-mono text-sm bg-white"
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            navigator.clipboard.writeText(`${window.location.origin}/api/webhook/aisensy-gradbud`);
                            toast.success('Gradbud AiSensy webhook URL copied!');
                          }}
                        >
                          <i className="fas fa-copy mr-2"></i>
                          Copy
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="text-sm text-gray-600">
                  <p><strong>Provider:</strong> AiSensy (replaced WATI for Gradbud)</p>
                  <p><strong>Lead Source:</strong> aisensy-gradbud</p>
                  <p><strong>Routing:</strong> Telecaller-first, then counsellor</p>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-4 text-sm text-blue-900">
                  <i className="fas fa-plug mr-2"></i>
                  AiSensy is an <strong>incoming</strong> webhook — there's no outbound "test" to run. Instead, use <strong>Check Webhook Activity</strong> below to confirm AiSensy is actually reaching this server and see the latest message it sent.
                </div>

                <AiSensyWebhookCheck />

                {/* Counselor Selection for Gradbud */}
                <div className="border-t pt-4 mt-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                    <h4 className="font-medium text-gray-900 text-sm">Assign Counselors</h4>
                    <div className="flex space-x-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => selectAllCounselors('gradbud')}
                        disabled={loadingCounselors}
                        className="text-xs px-2 h-7"
                      >
                        <i className="fas fa-check-double mr-1"></i>
                        All
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => clearAllCounselors('gradbud')}
                        disabled={loadingCounselors}
                        className="text-xs px-2 h-7"
                      >
                        <i className="fas fa-times mr-1"></i>
                        Clear
                      </Button>
                    </div>
                  </div>
                  
                  {loadingCounselors ? (
                    <div className="text-center py-4 text-gray-500">
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      Loading counselors...
                    </div>
                  ) : counselors.length === 0 ? (
                    <div className="text-center py-4 text-gray-500">
                      No active counselors found
                    </div>
                  ) : (
                    <div className="space-y-2 mb-4 max-h-48 overflow-y-auto">
                      {counselors.map((counselor) => (
                        <label 
                          key={counselor.id} 
                          htmlFor={`gradbud-${counselor.id}`}
                          className="flex items-center gap-2 cursor-pointer p-1.5 hover:bg-gray-50 rounded"
                        >
                          <input
                            type="checkbox"
                            id={`gradbud-${counselor.id}`}
                            checked={gradbudCounselors.includes(counselor.id)}
                            onChange={() => toggleCounselor(counselor.id, 'gradbud')}
                            className="w-4 h-4 flex-shrink-0 text-purple-600 rounded focus:ring-purple-500"
                          />
                          <span className="text-xs sm:text-sm text-gray-700 truncate">
                            {counselor.full_name}
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                  
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <p className="text-xs text-gray-600">
                      {gradbudCounselors.length === 0 
                        ? "⚠️ No counselors - will assign to ALL" 
                        : `✅ ${gradbudCounselors.length} selected`}
                    </p>
                    <Button
                      onClick={() => handleSaveCounselorAssignment('gradbud')}
                      disabled={saving || loadingCounselors}
                      size="sm"
                      className="text-xs h-8"
                    >
                      {saving ? (
                        <>
                          <i className="fas fa-spinner fa-spin mr-1"></i>
                          Saving...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save mr-1"></i>
                          Save
                        </>
                      )}
                    </Button>
                  </div>
                </div>
            </IntegrationCollapsible>

            {/* Razorpay Integration */}
            <IntegrationCollapsible
              id="razorpay"
              name="Razorpay Payment Gateway"
              subtitle="Enable online payments in Student Portal"
              iconClass="fas fa-credit-card text-blue-600"
              iconBg="bg-blue-100"
              open={openIntegration === 'razorpay'}
              onToggle={toggleIntegration}
            >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="razorpay_key">Razorpay Key ID</Label>
                    <Input
                      id="razorpay_key"
                      placeholder="rzp_test_xxxxxxxxxx"
                      value={integrationSettings.razorpay_key_id}
                      onChange={(e) => setIntegrationSettings({...integrationSettings, razorpay_key_id: e.target.value})}
                      data-testid="razorpay-key-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="razorpay_secret">Razorpay Key Secret</Label>
                    <Input
                      id="razorpay_secret"
                      type="password"
                      placeholder="Your Razorpay secret key"
                      value={integrationSettings.razorpay_key_secret}
                      onChange={(e) => setIntegrationSettings({...integrationSettings, razorpay_key_secret: e.target.value})}
                      data-testid="razorpay-secret-input"
                    />
                  </div>
                </div>

                {/* Webhook auto-reconciliation */}
                <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50/60 p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <i className="fas fa-bolt text-blue-600"></i>
                    <span className="text-sm font-semibold text-slate-800">Auto-reconcile payments (Webhook)</span>
                  </div>
                  <p className="text-xs text-slate-600 leading-relaxed">
                    Add a webhook in your Razorpay Dashboard (Developers → Webhooks → Add New Webhook) so payments made via payment links are marked paid in the CRM automatically. Select the <strong>payment_link.paid</strong> event (and optionally <strong>payment_link.partially_paid</strong>). Use the URL and secret below.
                  </p>
                  <div>
                    <Label className="text-xs">Webhook URL (paste into Razorpay)</Label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        readOnly
                        value={`${window.location.origin}/api/webhook/razorpay`}
                        data-testid="razorpay-webhook-url"
                        className="bg-white text-xs"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          navigator.clipboard.writeText(`${window.location.origin}/api/webhook/razorpay`);
                          toast.success('Webhook URL copied!');
                        }}
                        data-testid="copy-razorpay-webhook-url"
                      >
                        <i className="fas fa-copy"></i>
                      </Button>
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="razorpay_webhook_secret" className="text-xs">Webhook Secret</Label>
                    <Input
                      id="razorpay_webhook_secret"
                      type="password"
                      placeholder="Same secret you entered in the Razorpay webhook setup"
                      value={integrationSettings.razorpay_webhook_secret}
                      onChange={(e) => setIntegrationSettings({...integrationSettings, razorpay_webhook_secret: e.target.value})}
                      data-testid="razorpay-webhook-secret-input"
                      className="bg-white"
                    />
                    <p className="text-[11px] text-slate-500 mt-1">Must exactly match the “Secret” you set in the Razorpay webhook dialog.</p>
                  </div>
                </div>

                <div className="flex space-x-2 mt-4">
                  <Button 
                    onClick={async () => {
                      try {
                        await apiClient.put('/student-auth/razorpay/config', {
                          key_id: integrationSettings.razorpay_key_id,
                          key_secret: integrationSettings.razorpay_key_secret,
                          webhook_secret: integrationSettings.razorpay_webhook_secret,
                          enabled: true
                        });
                        toast.success('Razorpay configuration saved!');
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Failed to save');
                      }
                    }}
                    data-testid="save-razorpay-btn"
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <i className="fas fa-save mr-2"></i>
                    Save Razorpay Keys
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => window.open('https://dashboard.razorpay.com/', '_blank')}
                  >
                    <i className="fas fa-external-link-alt mr-2"></i>
                    Dashboard
                  </Button>
                </div>
            </IntegrationCollapsible>

            {/* Meta Ads — Lead Forms (real-time webhook). Collapsible wrapper around the sub-component. */}
            <IntegrationCollapsible
              id="meta-lead-forms"
              name="Meta Lead Forms"
              subtitle="Real-time Facebook/Instagram lead webhook"
              iconClass="fab fa-meta text-blue-700"
              iconBg="bg-blue-100"
              open={openIntegration === 'meta-lead-forms'}
              onToggle={toggleIntegration}
            >
              <MetaLeadFormsSettings inline />
            </IntegrationCollapsible>

            {/* Website Form Bridge */}
            <IntegrationCollapsible
              id="website-form-bridge"
              name="Website Form Bridge"
              subtitle="Capture leads from your website forms"
              iconClass="fas fa-globe text-indigo-600"
              iconBg="bg-indigo-100"
              open={openIntegration === 'website-form-bridge'}
              onToggle={toggleIntegration}
            >
              <WebsiteFormBridgeSettings inline />
            </IntegrationCollapsible>

            {/* Meta Marketing API Integration */}
            <IntegrationCollapsible
              id="meta-marketing-api"
              name="Meta Marketing API"
              subtitle="Fetch campaign data for CTWA leads automatically"
              iconClass="fab fa-facebook text-blue-600"
              iconBg="bg-blue-100"
              badge={<MetaConnectionStatus />}
              open={openIntegration === 'meta-marketing-api'}
              onToggle={toggleIntegration}
            >
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <i className="fas fa-info-circle text-blue-600 mt-1"></i>
                    <div className="flex-1">
                      <h4 className="font-medium text-blue-900 mb-2">How it Works</h4>
                      <p className="text-sm text-blue-800">
                        When a lead arrives from Click-to-WhatsApp (CTWA) ads via WATI, the CRM automatically 
                        fetches the Campaign Name, Ad Set Name, and Ad Name from Meta Marketing API using the Ad ID.
                        This enables full funnel tracking from ad → lead → admission → revenue.
                      </p>
                    </div>
                  </div>
                </div>

                <MetaIntegrationSettings />
                
                <div className="flex gap-2">
                  <Button 
                    variant="outline"
                    onClick={() => window.open('/campaign-analytics', '_self')}
                  >
                    <i className="fas fa-chart-bar mr-2"></i>
                    View Campaign Analytics
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => window.open('https://developers.facebook.com/docs/marketing-api/', '_blank')}
                  >
                    <i className="fas fa-external-link-alt mr-2"></i>
                    Meta API Docs
                  </Button>
                </div>
            </IntegrationCollapsible>

            {/* Salestrail Call Tracking Integration */}
            <IntegrationCollapsible
              id="salestrail"
              name="Salestrail Call Tracking"
              subtitle="Sync mobile SIM calls automatically to leads"
              iconClass="fas fa-phone-alt text-green-600"
              iconBg="bg-green-100"
              badge={<SalestrailConnectionStatus />}
              open={openIntegration === 'salestrail'}
              onToggle={toggleIntegration}
            >
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <i className="fas fa-info-circle text-green-600 mt-1"></i>
                    <div className="flex-1">
                      <h4 className="font-medium text-green-900 mb-2">How it Works</h4>
                      <p className="text-sm text-green-800">
                        Salestrail app records all calls made from staff mobile phones. This integration 
                        syncs those calls to the CRM, matching them to leads by phone number. Track 
                        call duration, direction (inbound/outbound), and connection status automatically.
                      </p>
                    </div>
                  </div>
                </div>

                <SalestrailIntegrationSettings />
                
                <div className="flex gap-2">
                  <Button 
                    variant="outline"
                    onClick={() => window.open('https://www.salestrail.io/', '_blank')}
                  >
                    <i className="fas fa-external-link-alt mr-2"></i>
                    Salestrail Website
                  </Button>
                </div>
            </IntegrationCollapsible>

            <div className="flex justify-end">
              <Button 
                onClick={handleSaveIntegrations}
                disabled={saving}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="save-integrations-button"
              >
                {saving ? (
                  <>
                    <div className="spinner mr-2"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="fas fa-save mr-2"></i>
                    Save All Integrations
                  </>
                )}
              </Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="notifications">
          <div className="space-y-6">
            <NotificationPreferences user={user} />
            {user?.role === 'admin' && <StudentWATINotifications />}
          </div>
        </TabsContent>

        <TabsContent value="general">
          <div className="space-y-6">
            <FixStudentIdsCard />
            <Card>
              <CardHeader>
                <CardTitle>Organization Settings</CardTitle>
                <CardDescription>Basic information about your organization</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="org_name">Organization Name</Label>
                    <Input
                      id="org_name"
                      data-testid="org-name-input"
                      value={orgSettings.org_name}
                      onChange={(e) => setOrgSettings({ ...orgSettings, org_name: e.target.value })}
                      placeholder="Enter organization name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="org_email">Contact Email</Label>
                    <Input
                      id="org_email"
                      data-testid="org-email-input"
                      type="email"
                      value={orgSettings.org_email}
                      onChange={(e) => setOrgSettings({ ...orgSettings, org_email: e.target.value })}
                      placeholder="Enter contact email"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="org_phone">Phone Number</Label>
                    <Input
                      id="org_phone"
                      data-testid="org-phone-input"
                      value={orgSettings.org_phone}
                      onChange={(e) => setOrgSettings({ ...orgSettings, org_phone: e.target.value })}
                      placeholder="Enter phone number"
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="org_address">Address</Label>
                  <Textarea
                    id="org_address"
                    data-testid="org-address-input"
                    value={orgSettings.org_address}
                    onChange={(e) => setOrgSettings({ ...orgSettings, org_address: e.target.value })}
                    placeholder="Enter your organization address"
                    rows={3}
                  />
                </div>
                
                <Button
                  data-testid="save-org-settings-btn"
                  onClick={handleSaveOrgSettings}
                  disabled={savingOrg}
                  className="bg-indigo-600 hover:bg-indigo-700"
                >
                  {savingOrg ? (
                    <><i className="fas fa-spinner fa-spin mr-2"></i>Saving...</>
                  ) : (
                    <><i className="fas fa-save mr-2"></i>Save Organization Settings</>
                  )}
                </Button>
              </CardContent>
            </Card>

            <Card data-testid="student-portal-access-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <i className="fas fa-graduation-cap text-[#0DA891]"></i>
                  Student Portal Access
                  <Badge variant="outline" className="ml-2 text-[10px] uppercase tracking-wide border-amber-300 text-amber-700 bg-amber-50">
                    Trial
                  </Badge>
                </CardTitle>
                <CardDescription>
                  Globally enable or disable the Student Portal login. When disabled, existing students cannot sign in.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="flex items-center justify-between rounded-lg border p-4 bg-slate-50">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-slate-900">
                        {studentPortalEnabled ? 'Enabled' : 'Disabled'}
                      </span>
                      <span className={`inline-block h-2 w-2 rounded-full ${studentPortalEnabled ? 'bg-emerald-500' : 'bg-rose-500'}`}></span>
                    </div>
                    <p className="text-xs text-slate-600">
                      {studentPortalEnabled
                        ? 'Students can log in and access their portal.'
                        : 'Student logins are blocked. Admins can still view student data.'}
                    </p>
                  </div>
                  <Switch
                    data-testid="student-portal-toggle"
                    checked={studentPortalEnabled}
                    onCheckedChange={handleToggleStudentPortal}
                    disabled={savingStudentPortal || loadingStudentPortal}
                  />
                </div>

                <div>
                  <Label htmlFor="student_portal_disabled_message">Message shown to students when portal is disabled</Label>
                  <Textarea
                    id="student_portal_disabled_message"
                    data-testid="student-portal-disabled-message"
                    value={studentPortalMessage}
                    onChange={(e) => setStudentPortalMessage(e.target.value)}
                    placeholder="Student portal access is currently unavailable. Please contact support."
                    rows={2}
                    className="mt-1"
                  />
                  <div className="mt-2 flex justify-end">
                    <Button
                      data-testid="save-student-portal-message-btn"
                      variant="outline"
                      size="sm"
                      onClick={handleSaveStudentPortalMessage}
                      disabled={savingStudentPortal}
                    >
                      {savingStudentPortal ? (
                        <><i className="fas fa-spinner fa-spin mr-2"></i>Saving...</>
                      ) : (
                        <><i className="fas fa-save mr-2"></i>Save Message</>
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <i className="fas fa-broom text-amber-500"></i>
                  Data Cleanup
                </CardTitle>
                <CardDescription>
                  One-click utilities to normalize legacy data. Safe to run repeatedly (idempotent).
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-lg border bg-slate-50 p-3">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-gray-800">Normalize Country Names</div>
                    <p className="text-xs text-gray-600 mt-0.5">
                      Fixes duplicate country entries like <span className="font-mono bg-white px-1 rounded">GEORGIA</span>, <span className="font-mono bg-white px-1 rounded">Georgia</span>, <span className="font-mono bg-white px-1 rounded">georgia</span> by converting them all to canonical case.
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      if (!window.confirm('Run country name normalization on all student records? This fixes case-variant duplicates and is safe to run multiple times.')) return;
                      try {
                        const res = await apiClient.post('/admin/normalize-countries');
                        const { fixes_applied, unresolved_count } = res.data;
                        const total = (fixes_applied?.students || 0) + (fixes_applied?.student_portals || 0);
                        if (total > 0) {
                          toast.success(`Fixed ${total} record${total > 1 ? 's' : ''}. ${unresolved_count ? `${unresolved_count} value(s) need manual review.` : ''}`);
                        } else {
                          toast.info(unresolved_count ? `No duplicates found. ${unresolved_count} value(s) need manual review.` : 'All country values are already normalized');
                        }
                        if (unresolved_count > 0 && res.data.unresolved_values?.length) {
                          console.log('Unresolved country values:', res.data.unresolved_values);
                        }
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Failed to normalize');
                      }
                    }}
                    className="shrink-0 border-amber-300 text-amber-800 hover:bg-amber-50"
                    data-testid="normalize-countries-btn"
                  >
                    <i className="fas fa-magic mr-2"></i>Run Normalization
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>System Information</CardTitle>
                <CardDescription>Current system status and version information</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">99.9%</div>
                    <div className="text-sm text-green-700">Uptime</div>
                  </div>
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">v1.0.0</div>
                    <div className="text-sm text-blue-700">Version</div>
                  </div>
                  <div className="text-center p-4 bg-purple-50 rounded-lg">
                    <div className="text-2xl font-bold text-purple-600">24/7</div>
                    <div className="text-sm text-purple-700">Support</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

          </div>
        </div>
      </Tabs>

      {/* Add Intake Dialog */}
      <Dialog open={showAddIntakeDialog} onOpenChange={setShowAddIntakeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Intake Period</DialogTitle>
            <DialogDescription>Create a new intake period for student enrollment</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="intake-name">Intake Name *</Label>
              <Input
                id="intake-name"
                placeholder="e.g., Spring 2026, Fall 2026"
                value={newIntake.name}
                onChange={(e) => setNewIntake({...newIntake, name: e.target.value})}
                data-testid="new-intake-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="intake-start">Start Date (Optional)</Label>
                <Input
                  id="intake-start"
                  type="date"
                  value={newIntake.start_date}
                  onChange={(e) => setNewIntake({...newIntake, start_date: e.target.value})}
                  data-testid="new-intake-start-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="intake-end">End Date (Optional)</Label>
                <Input
                  id="intake-end"
                  type="date"
                  value={newIntake.end_date}
                  onChange={(e) => setNewIntake({...newIntake, end_date: e.target.value})}
                  data-testid="new-intake-end-input"
                />
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Label htmlFor="intake-status">Status</Label>
              <select
                id="intake-status"
                value={newIntake.status}
                onChange={(e) => setNewIntake({...newIntake, status: e.target.value})}
                className="text-sm px-3 py-1.5 rounded-md border border-gray-300 focus:outline-none focus:ring-1 focus:ring-green-300"
                data-testid="new-intake-status-select"
              >
                <option value="Active">Active</option>
                <option value="Upcoming">Upcoming</option>
                <option value="Closed">Closed</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddIntakeDialog(false)}>Cancel</Button>
            <Button onClick={addIntakePeriod} disabled={savingIntake} className="bg-green-600 hover:bg-green-700" data-testid="save-new-intake-btn">
              {savingIntake ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-2"></i>
                  Saving...
                </>
              ) : (
                <>
                  <i className="fas fa-plus mr-2"></i>
                  Add Intake
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Intake Dialog */}
      <Dialog open={!!editingIntake} onOpenChange={(open) => !open && setEditingIntake(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Intake Period</DialogTitle>
            <DialogDescription>Update intake period details</DialogDescription>
          </DialogHeader>
          {editingIntake && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-intake-name">Intake Name *</Label>
                <Input
                  id="edit-intake-name"
                  value={editingIntake.name}
                  onChange={(e) => setEditingIntake({...editingIntake, name: e.target.value})}
                  data-testid="edit-intake-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-intake-start">Start Date (Optional)</Label>
                  <Input
                    id="edit-intake-start"
                    type="date"
                    value={editingIntake.start_date || ''}
                    onChange={(e) => setEditingIntake({...editingIntake, start_date: e.target.value})}
                    data-testid="edit-intake-start-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-intake-end">End Date (Optional)</Label>
                  <Input
                    id="edit-intake-end"
                    type="date"
                    value={editingIntake.end_date || ''}
                    onChange={(e) => setEditingIntake({...editingIntake, end_date: e.target.value})}
                    data-testid="edit-intake-end-input"
                  />
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Label htmlFor="edit-intake-status">Status</Label>
                <select
                  id="edit-intake-status"
                  value={editingIntake.status || (editingIntake.is_active ? 'Active' : 'Closed')}
                  onChange={(e) => setEditingIntake({...editingIntake, status: e.target.value})}
                  className="text-sm px-3 py-1.5 rounded-md border border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-300"
                  data-testid="edit-intake-status-select"
                >
                  <option value="Active">Active</option>
                  <option value="Upcoming">Upcoming</option>
                  <option value="Closed">Closed</option>
                </select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingIntake(null)}>Cancel</Button>
            <Button onClick={updateIntakePeriod} disabled={savingIntake} className="bg-blue-600 hover:bg-blue-700" data-testid="save-edit-intake-btn">
              {savingIntake ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-2"></i>
                  Saving...
                </>
              ) : (
                <>
                  <i className="fas fa-save mr-2"></i>
                  Save Changes
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Settings;